import { useEffect, useState, useRef, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import * as XLSX from 'xlsx';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import { getSocket } from '../socket';
import { getKstDateString } from '../utils/dateKst';

const COLORS = ['BK','BL','RD','OG','GF','YE'];
// ⚠ 여기 값들은 "목록추가/삭제 > 수정"에서 <input type="color">의 기본값으로도 그대로 쓰입니다.
// 그 입력은 브라우저가 반드시 6자리 전체 HEX(#RRGGBB)만 허용해서, 3자리 축약형(#fff/#222)을
// 넣으면 "유효한 CSS 색상이 아니다"라는 콘솔 경고가 뜨고 브라우저가 값을 임의로(보통 검정으로)
// 바꿔버립니다 — 그래서 항상 6자리로 풀어서 씁니다.
const COLOR_STYLE = {
  BK:{ bg:'#222222', color:'#ffffff' },
  BL:{ bg:'#1d4ed8', color:'#ffffff' },
  RD:{ bg:'#dc2626', color:'#ffffff' },
  OG:{ bg:'#ea580c', color:'#ffffff' },
  GF:{ bg:'#84cc16', color:'#ffffff' },
  YE:{ bg:'#facc15', color:'#222222' },
};

const thStyle = (bg, color) => ({
  padding:'10px 12px', background: bg||'#f8fafc', fontSize:12, fontWeight:600,
  color: color||'#666', borderBottom:'1px solid #e5e7eb',
  textAlign:'center', whiteSpace:'nowrap'
});
const thLeft = (bg, color) => ({ ...thStyle(bg, color), textAlign:'left' });
const tdStyle = { padding:'10px 12px', fontSize:13, borderBottom:'1px solid #f0f0f0', verticalAlign:'middle' };
const smallBtn = (bg) => ({
  background:bg, color:'#fff', border:'none', borderRadius:6,
  padding:'4px 10px', fontSize:11, cursor:'pointer', fontWeight:500
});
const formatNum = (n) => (n || 0).toLocaleString();

// ── 상세옵션(색상) 스타일/라벨 헬퍼 ────────────────────────────────
// 기존 고정 6색상(BK/BL/RD/OG/GF/YE)은 COLOR_STYLE을 그대로 쓰고, 제품등록에서
// 자유롭게 입력한 새 옵션(예: "검정")은 이름 자체를 라벨로 쓰고, 이름 기반 해시로 팔레트에서
// 색을 하나 골라 항상 같은 옵션이 같은 색으로 보이게 합니다.
const NEW_OPTION_PALETTE = [
  { bg:'#334155', color:'#fff' }, { bg:'#2563eb', color:'#fff' }, { bg:'#dc2626', color:'#fff' },
  { bg:'#d97706', color:'#fff' }, { bg:'#65a30d', color:'#fff' }, { bg:'#ca8a04', color:'#222' },
  { bg:'#7c3aed', color:'#fff' }, { bg:'#0d9488', color:'#fff' }, { bg:'#db2777', color:'#fff' },
];
const paletteIdx = (name) => {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % NEW_OPTION_PALETTE.length;
};
// 완제품 탭의 "목록추가/삭제"에서 항목별로 배경색/글자색을 직접 지정할 수 있다(완제품에서만
// 활성화되는 기능). 지정된 값이 있으면 최우선으로 쓰고, 없으면 기존처럼 고정 팔레트에서 이름
// 기반 해시로 자동 배정한다. Inventory 컴포넌트가 목록을 불러올 때마다 이 오버라이드 맵을
// 갱신해두면, 이 파일 안의 다른 모든 모달(재고수정/불량등록/불량목록 등)도 module-scope 함수인
// getOptionStyle을 그대로 호출하는 것만으로 자동으로 커스텀 색상을 반영하게 된다.
let OPTION_COLOR_OVERRIDES = {};
// 검정/파랑/빨강 등 미리 정의해둔 이름이 아닌, "목록추가/삭제"에서 새로 추가한 항목(예: test)은
// 더 이상 팔레트에서 무작위(해시 기반)로 색을 배정하지 않고, 중립적인 기본값(연한 회색 배경 +
// 짙은 회색 글자)으로 시작합니다. 관리자가 "수정"에서 원하는 색으로 직접 지정하면 됩니다.
const NEW_OPTION_DEFAULT = { bg:'#f8fafc', color:'#666666' };
const baseOptionStyle = (name) => COLOR_STYLE[name] || NEW_OPTION_DEFAULT;
const getOptionStyle = (name) => {
  const o = OPTION_COLOR_OVERRIDES[name];
  const base = baseOptionStyle(name);
  if (!o) return base;
  return { bg: o.bg || base.bg, color: o.color || base.color };
};
// 재고관리 제품옵션 목록에 등록한 값(예: BK)을 검정/파랑처럼 번역하지 않고, 영업관리/출고관리/
// 생산관리를 포함한 모든 화면에서 등록된 값 그대로 사용합니다.
const getOptionLabel = (name) => name;

export default function Inventory() {
  const [tab, setTab]               = useState('product');
  const [products, setProducts]     = useState([]);
  const [parts, setParts]           = useState([]);
  const [supplies, setSupplies]     = useState([]);
  // 자재발주관리 — 금형/지그 제작용 원자재·부품 발주 목록 + 단가표.
  const [materialOrders, setMaterialOrders] = useState([]);
  // 2026-09(원자재 탭 개편) — 요청사항: "자재발주관리" 탭을 없애고 소모품 우측에 "원자재" 탭으로
  // 통합. 등록/수정 폼과 단가표 모달을 완제품/부품/소모품과 동일하게 헤더의 공용 버튼
  // (제품등록→자재발주, 수정이력조회 왼쪽에 단가표 관리)에서 열도록 상태를 이 상위 컴포넌트로
  // 끌어올렸다(기존에는 MaterialOrderTab 내부에 있었음).
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [materialEditData, setMaterialEditData] = useState(null);
  const [showMaterialPriceManager, setShowMaterialPriceManager] = useState(false);
  // 2026-09(원자재 탭 UI개선) — 전체/자재/부품 구분 필터와 검색어를 탭 우측(목록추가/삭제 왼쪽)
  // 에서 조작하도록 상위 컴포넌트로 끌어올렸다(기존엔 MaterialOrderTab 내부에 있었음).
  const [materialFilterCategory, setMaterialFilterCategory] = useState('전체');
  const [materialSearch, setMaterialSearch] = useState('');
  // ── 제품군(안전벨트/안전블럭 등) — 완제품/부품/소모품 탭 "바로 위"에 있는 상위 탭.
  // groupTab에 선택된 제품군의 id가 들어가고, 완제품/부품/소모품 목록은 이 값으로 필터링해서
  // 보여준다. 향후 "+ 제품등록" 버튼으로 새 제품군을 계속 추가할 수 있다.
  const [groups, setGroups]         = useState([]);
  const [groupTab, setGroupTab]     = useState(null);
  const [showGroupManager, setShowGroupManager] = useState(false);
  // ── 재고현황 "목록"(완제품 상세옵션 / 부품·소모품 표시열) — 제품군+구분(완제품/부품/소모품)
  // 조합별로 서버에 저장되며, 저장한 적이 없으면 서버가 기본값(완제품은 6색상, 부품/소모품은
  // No/품번/품명/재질/재고수량/공급사/비고)을 내려준다.
  const [stockColumns, setStockColumns] = useState({ '완제품': [], '부품': [], '소모품': [], '원자재': [], '원자재_표시열': [] });
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [formType, setFormType]     = useState('');
  const [editData, setEditData]     = useState(null);
  const [editStock, setEditStock]   = useState(null);
  const [selectedProd, setSelectedProd] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [showHistory, setShowHistory]   = useState(false);
  const [history, setHistory]           = useState([]);
  // 부품/소모품 표에서 개별 항목의 "수정이력 조회"를 눌렀을 때 대상 품목({name, part_no}).
  const [itemHistoryTarget, setItemHistoryTarget] = useState(null);
  const [msg, setMsg]               = useState('');
  const [defectSummary, setDefectSummary] = useState([]); // [{product_id, color, total}]
  const [defectLatest, setDefectLatest]   = useState([]); // [{product_id, defect_date, created_by}]
  const [defectRegister, setDefectRegister] = useState(null); // { product, color } 등록모달
  const [defectListView, setDefectListView] = useState(null); // { product, color } 목록모달

  const user         = JSON.parse(sessionStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'superadmin' || user.role === 'admin';

  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다(hasDetail이 내부적으로 최고관리자
  // 여부를 이미 처리합니다).
  const { hasDetail } = usePermissions();
  const canReadProduct  = hasDetail('inventory', 'finished_goods_read');
  const canReadSupply   = hasDetail('inventory', 'consumables_read');
  const canReadParts    = hasDetail('inventory', 'parts_read');
  const canReadHistory  = hasDetail('inventory', 'history_read');
  const canWriteProduct = hasDetail('inventory', 'finished_goods_write');
  const canWriteSupply  = hasDetail('inventory', 'consumables_write');
  const canWriteParts   = hasDetail('inventory', 'parts_write');
  // 부품/소모품 "수정"은 등록/삭제와 별개 권한(부품수정/소모품 수정)으로 분리되어 있습니다.
  const canEditParts    = hasDetail('inventory', 'parts_edit');
  const canEditSupply   = hasDetail('inventory', 'consumables_edit');
  const canDefect       = hasDetail('inventory', 'defect_register');
  // 제품군 등록/삭제와 제품옵션(완제품/부품/소모품 목록) 추가·삭제는 예전에는 하나의
  // 세부권한 키로 묶여 있었지만, 서로 다른 권한으로 분리되었습니다.
  const canGroupManage  = hasDetail('inventory', 'product_group_manage');
  const canOptionManage = hasDetail('inventory', 'product_option_manage');
  const canDeleteHistory = hasDetail('inventory', 'history_delete');
  // 자재발주관리(소모품 탭 우측 신규 탭) — 목록보기/등록·수정·삭제 권한을 별도로 분리.
  const canReadMaterialOrder  = hasDetail('inventory', 'material_order_read');
  const canWriteMaterialOrder = hasDetail('inventory', 'material_order_write');

  const loadProducts = () => API.get('/stock/products').then(r => setProducts(r.data)).catch(()=>{});
  const loadParts    = () => API.get('/stock/parts').then(r => setParts(r.data)).catch(()=>{});
  const loadSupplies = () => API.get('/stock/supplies').then(r => setSupplies(r.data)).catch(()=>{});
  const loadMaterialOrders = () => API.get('/stock/material-orders').then(r => setMaterialOrders(r.data)).catch(()=>{});
  const loadHistory  = () => API.get('/stock/history', { params: groupTab ? { group_id: groupTab } : {} })
    .then(r => setHistory(r.data)).catch(()=>{});
  // 부품/소모품 표에서 "수정이력 조회"를 누르면, 전체 이력을 다시 불러온 뒤 그 품목의
  // 품번(모델번호) 기준으로 걸러 보여줍니다(품번이 비어있으면 품명으로 대신 매칭).
  const openItemHistory = (item) => { loadHistory(); setItemHistoryTarget(item); };
  // 수정이력을 지운 뒤에는 그 이력 목록만 새로고침해서는 부족합니다. 지운 이력이 어떤 품목의
  // "가장 최근 수정"이었다면, 완제품/부품/소모품 표의 "업데이트 이력" 열(products/parts에 저장된
  // updated_at·updated_by)도 서버에서 함께 갱신되므로, 목록들도 같이 다시 불러와야 화면에 바로
  // 반영됩니다.
  const onHistoryDeleted = () => { loadHistory(); loadProducts(); loadParts(); loadSupplies(); };
  const itemHistoryRecords = itemHistoryTarget
    ? history.filter(h => itemHistoryTarget.part_no
        ? h.model_no === itemHistoryTarget.part_no
        : h.item_name === itemHistoryTarget.name)
    : [];
  const loadDefectSummary = () => API.get('/stock/defects/summary')
    .then(r => { setDefectSummary(r.data.totals||[]); setDefectLatest(r.data.latest||[]); }).catch(()=>{});
  const loadGroups = () => API.get('/stock/groups').then(r => setGroups(r.data)).catch(()=>{});
  // 원자재(자재발주관리)는 제품군(group_id)에 속하지 않는 전사 공통 목록이므로, 다른
  // 완제품/부품/소모품 목록과 달리 groupTab에 의존하지 않고 고정 그룹ID(0)로 저장/조회한다.
  const MATERIAL_COLUMNS_GROUP_ID = 0;
  const loadColumns = (category) => {
    if (category === '원자재' || category === '원자재_표시열') {
      API.get('/stock/columns', { params: { group_id: MATERIAL_COLUMNS_GROUP_ID, category } })
        .then(r => setStockColumns(prev => ({ ...prev, [category]: r.data.items || [] })))
        .catch(()=>{});
      return;
    }
    if (!groupTab) return;
    API.get('/stock/columns', { params: { group_id: groupTab, category } })
      .then(r => setStockColumns(prev => ({ ...prev, [category]: r.data.items || [] })))
      .catch(()=>{});
  };

  const defectQty = (productId, color) => {
    const row = defectSummary.find(d => d.product_id === productId && d.color === color);
    return row ? row.total : 0;
  };
  const defectLatestFor = (productId) => defectLatest.find(d => d.product_id === productId);

  useEffect(() => { loadProducts(); loadParts(); loadSupplies(); loadDefectSummary(); loadGroups(); loadMaterialOrders(); loadColumns('원자재'); loadColumns('원자재_표시열'); }, []);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 재고관리(완제품) 항목을 클릭해 들어온 경우, 그
  // 제품이 속한 제품군 탭으로 전환하고 수정폼을 바로 연다(완제품은 별도 "상세보기" 화면이 없고
  // 등록/수정 폼이 곧 상세화면 역할을 한다). products가 아직 로드되기 전일 수 있으므로 대기.
  const feedLocation = useLocation();
  const feedNavigate = useNavigate();
  useEffect(() => {
    const ao = feedLocation.state?.activityOpen;
    if (ao && products.length) {
      const found = products.find(p => String(p.id) === String(ao.itemId));
      if (found) {
        setTab('product');
        if (found.group_id != null) setGroupTab(found.group_id);
        setEditData(found); setFormType('product'); setShowForm(true);
      }
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    }
  }, [feedLocation.state, products]); // eslint-disable-line

  // 생산관리(다른 사용자 화면 포함)에서 생산수량 반영 등으로 완제품 재고(product_stock)가
  // 바뀔 때마다 서버가 socket.io로 'stock:updated'를 브로드캐스트한다. 여기서는 새로고침 없이
  // 최신 재고 수치를 즉시 다시 불러오기만 하면 된다(그룹 필터링은 하지 않고 항상 재조회 —
  // 목록 전체를 다시 받아도 부담이 적고 놓치는 케이스가 없다).
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onStockUpdated = () => { loadProducts(); loadDefectSummary(); };
    socket.on('stock:updated', onStockUpdated);
    return () => socket.off('stock:updated', onStockUpdated);
  }, []);

  // 제품군 목록이 처음 로드되면 첫 번째 제품군(안전벨트)을 기본 선택 탭으로 지정
  useEffect(() => {
    if (groupTab === null && groups.length) setGroupTab(groups[0].id);
  }, [groups, groupTab]);

  // 선택된 제품군이 바뀔 때마다 완제품/부품/소모품 3개 구분의 "목록"을 모두 새로 불러온다
  useEffect(() => {
    if (!groupTab) return;
    loadColumns('완제품'); loadColumns('부품'); loadColumns('소모품');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupTab]);

  // 완제품/부품/소모품 목록을 현재 선택된 제품군 기준으로 필터링해서 화면/인쇄/내보내기에 사용
  // (안전블럭처럼 아직 등록된 품목이 없는 제품군은 자연스럽게 빈 목록이 되어, 기존 "등록된
  // 항목이 없습니다" 안내만 보이는 방식으로 화면 포맷만 잡힌 상태가 된다)
  const groupProducts = products.filter(p => p.group_id === groupTab);
  const groupParts    = parts.filter(p => p.group_id === groupTab);
  const groupSupplies = supplies.filter(p => p.group_id === groupTab);
  const currentGroupName = groups.find(g => g.id === groupTab)?.name || '';

  // 제품군별 등록된 품목(완제품+부품+소모품) 수 — "제품군 등록/삭제" 모달에서 삭제 전에
  // 미리 보여주기 위함(품목이 남아있는 제품군은 백엔드에서도 삭제를 막지만, 저장을 눌러보기
  // 전에 화면에서 먼저 알 수 있게 한다).
  const groupItemCounts = (() => {
    const counts = {};
    groups.forEach(g => {
      counts[g.id] = products.filter(p => p.group_id === g.id).length
        + parts.filter(p => p.group_id === g.id).length
        + supplies.filter(p => p.group_id === g.id).length;
    });
    return counts;
  })();

  // 완제품의 상세옵션(색상 등)은 더 이상 제품마다 따로 입력하지 않고, 이 제품군의 "목록"
  // (상단 "목록 추가/삭제"에서 관리, stockColumns['완제품'])을 모든 완제품이 공통으로 그대로
  // 사용합니다. 개별 제품에 실제 재고 데이터가 없는 옵션 칸은 "-"로 비워둡니다.
  const optionColumns = (stockColumns['완제품'] || []).map(c => c.name);
  const hasOption = (p, c) => !!(p.stocks && Object.prototype.hasOwnProperty.call(p.stocks, c));

  // 완제품 "목록"에 저장된 항목별 배경색/글자색을 module-level 오버라이드 맵에 동기화한다.
  // getOptionStyle()은 이 파일의 여러 컴포넌트(StockEdit/DefectRegisterModal/DefectListModal 등)에서
  // props 없이 직접 호출되므로, 이렇게 한 곳에서만 갱신해두면 어디서든 커스텀 색상이 바로 반영된다.
  // ⚠ 예전에는 이 동기화를 useEffect로 했었는데, effect는 "렌더가 끝난 뒤"에 실행되기 때문에
  // stockColumns가 막 바뀌어서 다시 그려지는 그 렌더 자체는 여전히 갱신 전 값을 사용해버리는
  // 문제가 있었다(저장 직후엔 기본색이 보이고, 완제품→부품/소모품→완제품처럼 탭을 한 번 더
  // 전환해서 추가 렌더가 일어나야 비로소 지정색이 반영됨). useEffect 없이 렌더 함수 본문에서
  // 바로 계산해두면, 이 렌더 안에서 그려지는 getOptionStyle 호출부터 항상 최신 값을 쓴다.
  const _colorOverrideMap = {};
  (stockColumns['완제품'] || []).forEach(c => {
    if (c && (c.bg_color || c.text_color)) _colorOverrideMap[c.name] = { bg: c.bg_color, color: c.text_color };
  });
  OPTION_COLOR_OVERRIDES = _colorOverrideMap;

  const showMsg = (text) => { setMsg(text); setTimeout(()=>setMsg(''), 3000); };

  const deleteProduct = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/stock/products/' + id);
    setSelectedProd(null); showMsg('삭제 완료!'); loadProducts();
  };
  const deletePart = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/stock/parts/' + id);
    showMsg('삭제 완료!'); loadParts(); loadSupplies();
  };
  // 부품/소모품 표에서 드래그 앤 드롭으로 순서를 바꾸면, 그 화면에 보이고 있던(=현재 선택된
  // 제품군의) 항목 id들을 화면에 보이는 새 순서 그대로 서버에 보내 sort_order를 일괄 갱신한다.
  const reorderParts = async (idsInOrder, isSupply) => {
    try {
      await API.put('/stock/parts/reorder', { order: idsInOrder });
      if (isSupply) loadSupplies(); else loadParts();
    } catch (err) {
      alert(err.response?.data?.message || '순서 변경 중 오류가 발생했습니다.');
    }
  };

  // ── 수정이력 엑셀 내보내기
  const exportHistory = (records = history, label = '') => {
    const rows = records.map(h => ({
      '구분':      h.category || '',
      '모델번호':  h.model_no || '',
      '옵션':      h.color || '-',
      '수정전':    h.before_qty !== null ? h.before_qty : '-',
      '수정후':    h.after_qty  !== null ? h.after_qty  : '-',
      '수정자':    h.updated_by || '',
      '수정일시':  h.updated_at || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:8},{wch:14},{wch:10},{wch:8},{wch:8},{wch:10},{wch:22}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '수정이력');
    const filename = `IMS_재고수정이력${label ? '_' + label : ''}.xlsx`;
    XLSX.writeFile(wb, filename);
    logDownload('재고관리', filename);
  };

  // ── 인쇄
  const handlePrint = () => {
    const orientation = tab === 'product' ? 'landscape' : 'portrait';
    const groupPrefix = currentGroupName ? `${currentGroupName} - ` : '';
    const productHTML = `
      <h2 style="text-align:center;margin-bottom:12px;">${groupPrefix}완제품 재고현황</h2>
      <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th colspan="2" style="text-align:center;">완제품</th>
          ${optionColumns.map(c=>`<th style="text-align:center;">${getOptionLabel(c)}</th>`).join('')}
        </tr></thead>
        <tbody>
          ${groupProducts.map(p=>`<tr>
            <td style="font-weight:600">${p.model_no}</td><td>${p.name}</td>
            ${optionColumns.map(c=> hasOption(p,c) ? `<td style="text-align:center;">${formatNum(p.stocks?.[c])}</td>` : `<td style="text-align:center;color:#ccc;">-</td>`).join('')}
          </tr>`).join('')}
        </tbody>
      </table>
      <h2 style="text-align:center;margin:20px 0 12px;">${groupPrefix}완제품 불량처리</h2>
      <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th colspan="2" style="text-align:center;">완제품</th>
          ${optionColumns.map(c=>`<th style="text-align:center;">${getOptionLabel(c)}</th>`).join('')}
        </tr></thead>
        <tbody>
          ${groupProducts.map(p=>`<tr>
            <td style="font-weight:600">${p.model_no}</td><td>${p.name}</td>
            ${optionColumns.map(c=> hasOption(p,c) ? `<td style="text-align:center;">${formatNum(defectQty(p.id,c))}</td>` : `<td style="text-align:center;color:#ccc;">-</td>`).join('')}
          </tr>`).join('')}
        </tbody>
      </table>`;
    const partHTML = `
      <h2 style="text-align:center;margin-bottom:12px;">${groupPrefix}부품 재고현황</h2>
      <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th>PART NUMBER</th><th>품명</th><th>규격/치수</th><th style="text-align:center;">재고현황</th>
        </tr></thead>
        <tbody>${groupParts.map(p=>`<tr>
          <td>${p.part_no||'-'}</td><td>${p.name}</td>
          <td>${p.dimension||'-'}</td><td style="text-align:center;">${formatNum(p.quantity)}</td>
        </tr>`).join('')}</tbody>
      </table>`;
    const supplyHTML = `
      <h2 style="text-align:center;margin-bottom:12px;">${groupPrefix}소모품 재고현황</h2>
      <table border="1" cellpadding="6" cellspacing="0" style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th>품번</th><th>품명</th><th>규격/치수</th><th style="text-align:center;">재고현황</th><th>비고</th>
        </tr></thead>
        <tbody>${groupSupplies.map(p=>`<tr>
          <td>${p.part_no||'-'}</td><td>${p.name}</td>
          <td>${p.dimension||'-'}</td><td style="text-align:center;">${formatNum(p.quantity)}</td>
          <td>${p.note||''}</td>
        </tr>`).join('')}</tbody>
      </table>`;
    const contentMap = { product:productHTML, part:partHTML, supply:supplyHTML };
    const now = new Date();
    const printDT = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printBy = printedByLabel(user);
    const footerHtml = `<div style="display:flex;justify-content:space-between;margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:11px;color:#555;">
        <span>출력일시: ${printDT}</span><span>출력자: ${printBy}</span>
      </div>`;
    const pw = window.open('','_blank','width=1200,height=900');
    pw.document.write(`<!DOCTYPE html><html><head><title>재고현황</title>
      <style>@page{size:A4 ${orientation};margin:0}body{font-family:sans-serif;margin:0;padding:10mm;box-sizing:border-box}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:5px 8px}</style>
      </head><body>${contentMap[tab]}${footerHtml}</body></html>`);
    pw.document.close(); pw.focus();
    logPrint('재고관리', `${currentGroupName}${currentGroupName?' ':''}${tab==='product'?'완제품 재고현황+불량처리':tab==='part'?'부품 재고현황':'소모품 재고현황'}`);
    setTimeout(()=>{ pw.print(); pw.close(); }, 500);
  };

  // ── 엑셀 내보내기
  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const prodRows = groupProducts.map(p => {
      const row = { '모델번호':p.model_no, '품명':p.name };
      optionColumns.forEach(c => { row[getOptionLabel(c)] = hasOption(p,c) ? (p.stocks?.[c]||0) : '-'; });
      row['최종수정일'] = p.updated_at||''; row['수정자'] = p.updated_by||'';
      return row;
    });
    const ws1 = XLSX.utils.json_to_sheet(prodRows);
    ws1['!cols'] = [{wch:14},{wch:16},...optionColumns.map(()=>({wch:12})),{wch:20},{wch:10}];
    XLSX.utils.book_append_sheet(wb, ws1, '완제품');
    const partRows = groupParts.map(p => ({
      'PART NUMBER':p.part_no||'-','품명':p.name,'규격/치수':p.dimension||'-','재고현황':p.quantity||0,
    }));
    const ws2 = XLSX.utils.json_to_sheet(partRows);
    ws2['!cols'] = [{wch:16},{wch:24},{wch:20},{wch:10}];
    XLSX.utils.book_append_sheet(wb, ws2, '부품');
    const supplyRows = groupSupplies.map(p => ({
      '품번':p.part_no||'-','품명':p.name,'규격/치수':p.dimension||'-','재고현황':p.quantity||0,'비고':p.note||'',
    }));
    const ws3 = XLSX.utils.json_to_sheet(supplyRows);
    ws3['!cols'] = [{wch:14},{wch:24},{wch:20},{wch:10},{wch:16}];
    XLSX.utils.book_append_sheet(wb, ws3, '소모품');
    const exportFileName = `IMS_재고현황${currentGroupName ? '_'+currentGroupName : ''}.xlsx`;
    XLSX.writeFile(wb, exportFileName);
    logDownload('재고관리', exportFileName);
  };

  const TAB_LIST = [
    canReadProduct && { val:'product', label:'완제품', count: products.filter(p => p.group_id === groupTab).length },
    canReadParts   && { val:'part',    label:'부품',   count: parts.filter(p => p.group_id === groupTab).length },
    canReadSupply  && { val:'supply',  label:'소모품', count: supplies.filter(p => p.group_id === groupTab).length },
    // 원자재(구 "자재발주관리") — 사용자 요청대로 소모품 탭 바로 우측에 위치(생산관리 측
    // 금형/지그 제작용 원자재·부품 발주 등록). 제품군과 무관하게 전체 발주 건수를 배지로 보여준다.
    canReadMaterialOrder && { val:'material_order', label:'원자재', count: materialOrders.length },
  ].filter(Boolean);
  // 지금 보고 있는 탭(완제품/부품/소모품)에 해당하는 "목록"(상세옵션 또는 표시열) 구분명.
  // 2026-09(원자재 탭 목록관리 재설계) — 원자재 탭의 "목록추가/삭제"는 더 이상 구분값(자재/부품)이
  // 아니라 이 표에 실제로 보여줄 열 목록('원자재_표시열')을 관리한다(부품/소모품과 동일한 방식).
  // 구분값(자재/부품) 관리는 재질별 단가표 관리 모달의 "구분 관리" 버튼으로 옮겨졌다.
  const currentCategory = tab === 'product' ? '완제품' : tab === 'part' ? '부품' : tab === 'material_order' ? '원자재_표시열' : '소모품';

  // 원자재 탭의 "구분" 값 목록 — 재질별 단가표 관리 모달의 "구분 관리" 버튼에서 관리하는
  // stockColumns['원자재']를 그대로 사용한다. 이 목록이 전체/구분 필터 탭, 자재발주 등록폼의
  // 구분 select, 목록의 구분 배지 색상, 단가표 관리 모달의 구분 select에 공통으로 반영된다.
  // 항목이 하나도 없을 때(초기값 등)는 기본값 자재/부품으로 대체한다.
  const materialCategories = (stockColumns['원자재'] && stockColumns['원자재'].length)
    ? stockColumns['원자재'].map(c => c.name)
    : ['자재', '부품'];
  const materialCategoryColors = {};
  (stockColumns['원자재'] || []).forEach(c => {
    if (c) materialCategoryColors[c.name] = { bg: c.bg_color || '#E6F1FB', color: c.text_color || '#185FA5' };
  });

  // 현재 탭이 읽기 권한 없는 탭이면(설정 변경 등으로) 볼 수 있는 첫 탭으로 이동
  useEffect(() => {
    if (!TAB_LIST.some(t => t.val === tab) && TAB_LIST.length) setTab(TAB_LIST[0].val);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadProduct, canReadParts, canReadSupply, canReadMaterialOrder]);

  return (
    <div style={{ padding:24, maxWidth:1690 }}>
      {/* ── 헤더 ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>📦 재고현황{currentGroupName ? ` · ${currentGroupName}` : ''}</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>완제품·부품·소모품 재고를 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={handlePrint}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8, padding:'10px 16px', fontSize:14, cursor:'pointer', fontWeight:600 }}>
            🖨️ 인쇄
          </button>
          <button onClick={handleExport}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8, padding:'10px 16px', fontSize:14, cursor:'pointer', fontWeight:600 }}>
            📥 내보내기
          </button>
          {canReadHistory && (
            <button
              onClick={() => { loadHistory(); setShowHistory(true); }}
              style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              📋 수정이력 조회
            </button>
          )}
          {tab === 'material_order' && canWriteMaterialOrder && (
            <button onClick={() => setShowMaterialPriceManager(true)}
              style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              💲 단가표 관리
            </button>
          )}
          {tab === 'material_order' ? (
            canWriteMaterialOrder && (
              <button onClick={() => { setMaterialEditData(null); setShowMaterialForm(true); }}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                + 자재발주
              </button>
            )
          ) : (
            (canWriteProduct || canWriteParts || canWriteSupply) && (
              <button onClick={() => { setShowForm(true); setEditData(null); setFormType(''); }}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                + 제품등록
              </button>
            )
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:14, fontSize:13 }}>{msg}</div>}

      {/* ── 제품군 탭 (안전벨트/안전블럭 등) — 완제품/부품/소모품 탭 바로 위에 위치.
          업무관리(Tasks.js) 구분 탭과 동일한 CSS(밑줄 + 건수 배지)를 사용한다. ── */}
      <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap',
        marginBottom:14, paddingBottom:10, borderBottom:'1px solid #eee' }}>
        {groups.map(g => {
          // 제품군 탭 자체에는 더 이상 숫자 배지를 붙이지 않습니다(건수는 아래 완제품/부품/소모품
          // 탭에 각각 표시합니다).
          const active = groupTab === g.id;
          return (
            <button key={g.id} onClick={() => { setGroupTab(g.id); setSelectedProd(null); setSelectedPart(null); }}
              style={{ padding:'10px 18px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                background:'none', color: active ? '#185FA5' : '#888' }}>
              {g.name}
            </button>
          );
        })}
        {canGroupManage && (
          <button onClick={() => setShowGroupManager(true)} title="제품군 등록/삭제"
            style={{ padding:'6px 12px', marginLeft:4, border:'1px dashed #ccc', borderRadius:20,
              cursor:'pointer', fontSize:13, fontWeight:600, background:'#fff', color:'#888' }}>
            + 제품군
          </button>
        )}
      </div>

      {/* ── 탭 ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, borderBottom:'2px solid #e5e7eb' }}>
        <div style={{ display:'flex' }}>
          {TAB_LIST.map(t => (
            <button key={t.val} onClick={() => { setTab(t.val); setSelectedProd(null); setSelectedPart(null); }}
              style={{ padding:'10px 24px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                borderBottom: tab===t.val ? '2px solid #185FA5':'2px solid transparent',
                background:'none', color: tab===t.val?'#185FA5':'#888', marginBottom:-2 }}>
              {t.label}
              {t.count > 0 && (
                <span style={{ fontSize:11, marginLeft:6, background: tab===t.val ? '#185FA5' : '#e5e7eb',
                  color: tab===t.val ? '#fff' : '#888', padding:'1px 7px', borderRadius:20 }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {tab === 'material_order' && canReadMaterialOrder && (
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
              <input
                type="text"
                value={materialSearch}
                onChange={e => setMaterialSearch(e.target.value)}
                placeholder="🔍 검색"
                style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:20, fontSize:13, width:160 }}
              />
              {['전체', ...materialCategories].map(c => (
                <button key={c} onClick={() => setMaterialFilterCategory(c)}
                  style={{ padding:'7px 14px', border:'none', borderRadius:20, cursor:'pointer', fontSize:13, fontWeight:600,
                    background: materialFilterCategory===c ? '#185FA5' : '#f0f0f0', color: materialFilterCategory===c ? '#fff' : '#555' }}>
                  {c === '전체' ? `전체 (${materialOrders.length})` : `${c} (${materialOrders.filter(o=>o.category===c).length})`}
                </button>
              ))}
            </div>
          )}
          {canOptionManage && (
            <button onClick={() => setShowColumnManager(true)}
              title={currentCategory === '완제품' ? '제품옵션 추가/삭제' : `${currentCategory} 목록 추가/삭제`}
              style={{ marginBottom:8, padding:'6px 12px', border:'1px dashed #ccc', borderRadius:20,
                cursor:'pointer', fontSize:13, fontWeight:600, background:'#fff', color:'#888' }}>
              {currentCategory === '완제품' ? '제품옵션 추가/삭제' : '목록추가/삭제'}
            </button>
          )}
        </div>
      </div>

      {/* ── 완제품 탭 ── */}
      {tab === 'product' && canReadProduct && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', tableLayout:'fixed',
            borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <thead>
              <tr>
                <th colSpan={2} style={{ ...thLeft('#f8fafc'), fontSize:14, width:260, minWidth:260, maxWidth:260 }}>완제품 재고현황</th>
                {/* 상세옵션 열: 폭을 고정하지 않아, table-layout:fixed 하에서 옵션 개수에 따라
                    남은 공간을 자동으로 균등배분("자동배분")합니다. 아직 옵션(=등록된 완제품)이
                    하나도 없는 제품군은 고정 색상 6개 대신 안내 문구 한 칸을 보여준다. */}
                {optionColumns.length > 0 ? optionColumns.map((c, idx) => (
                  <th key={c} style={{ ...thStyle(getOptionStyle(c, idx).bg, getOptionStyle(c, idx).color), fontSize:14 }}>
                    {getOptionLabel(c)}
                  </th>
                )) : (
                  <th style={{ ...thStyle('#f8fafc', '#aaa'), fontSize:13, fontWeight:500, fontStyle:'italic' }}>
                    제품옵션을 등록하세요
                  </th>
                )}
                {/* "업데이트 이력" 열: 완제품 쓰기권한(수정 가능)이나 "수정사항 보기" 읽기권한 둘 중
                    하나만 있어도 열 자체는 보이되, 실제 최근 수정 정보(날짜/작성자)는
                    "수정사항 보기" 권한이 있을 때만 표시된다(아래 canReadHistory 분기 참고).
                    클릭해서 실제로 편집하는 동작은 여전히 canWriteProduct로만 제어된다. */}
                {(canWriteProduct || canReadHistory) && <th style={{ ...thStyle('#f8fafc'), width:320, minWidth:320, maxWidth:320, fontSize:13 }}>업데이트 이력</th>}
              </tr>
            </thead>
            <tbody>
              {groupProducts.length === 0 ? (
                <tr><td colSpan={2+Math.max(optionColumns.length,1)+((canWriteProduct||canReadHistory)?1:0)} style={{ textAlign:'center', padding:'40px', color:'#aaa', fontSize:13 }}>
                  등록된 완제품 목록이 없습니다.
                </td></tr>
              ) : groupProducts.map((p, i) => (
                <>
                  <tr key={p.id}
                    style={{ background: selectedProd?.id===p.id?'#EFF6FF':i%2===0?'#fff':'#fafafa' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f6ff'}
                    onMouseLeave={e => e.currentTarget.style.background=selectedProd?.id===p.id?'#EFF6FF':i%2===0?'#fff':'#fafafa'}>

                    {/* 모델번호 + 품명 (클릭하면 수정/삭제 메뉴) */}
                    <td colSpan={2}
                      style={{ ...tdStyle, cursor: canWriteProduct?'pointer':'default', width:260, minWidth:260, maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                      onClick={() => canWriteProduct && setSelectedProd(selectedProd?.id===p.id ? null : p)}>
                      <div style={{ fontWeight:700, fontSize:14, color:'#1a1a1a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.model_no} <span style={{ fontWeight:500, color:'#888' }}>({p.name})</span></div>
                    </td>

                    {/* 색상(상세옵션)별 재고 (클릭 시 재고 수정) — 수량이 확인되지 않는 옵션(아직
                        이 제품에 재고 데이터가 없는 경우)도 "-"로 비워두지 않고 0으로 표기합니다. */}
                    {optionColumns.map(c => {
                      const qty = p.stocks?.[c] || 0;
                      const isShort = qty < 0;
                      return (
                        <td key={c}
                          style={{ ...tdStyle, textAlign:'center', cursor: canWriteProduct?'pointer':'default' }}
                          onClick={() => canWriteProduct && setEditStock({ product:p, color:c, qty:p.stocks?.[c]||0 })}>
                          <span style={{ fontSize:15, fontWeight:600, color: isShort ? '#dc2626' : qty>0?'#185FA5':'#ccc' }}>
                            {isShort ? '-' + formatNum(Math.abs(qty)) : formatNum(qty)}
                          </span>
                        </td>
                      );
                    })}

                    {/* 업데이트 이력: 최근 수정 날짜/작성자는 "수정사항 보기" 권한이 있을 때만 표시 */}
                    {(canWriteProduct || canReadHistory) && (
                      <td style={{ ...tdStyle, textAlign:'center', width:320, minWidth:320, maxWidth:320 }}>
                        {canReadHistory && p.updated_at ? (
                          <span style={{ fontSize:13, whiteSpace:'nowrap' }}>
                            <span style={{ color:'#185FA5', fontWeight:500, fontSize:14 }}>{p.updated_at}</span>
                            <span style={{ color:'#888' }}> · {p.updated_by}</span>
                          </span>
                        ) : (
                          <span style={{ fontSize:13, color:'#ccc' }}>-</span>
                        )}
                      </td>
                    )}
                  </tr>

                  {/* 수정/삭제 드롭다운 */}
                  {canWriteProduct && selectedProd?.id === p.id && (
                    <tr key={'action-'+p.id}>
                      <td colSpan={2+Math.max(optionColumns.length,1)+((canWriteProduct||canReadHistory)?1:0)}
                        style={{ padding:'10px 16px', background:'#EFF6FF', borderBottom:'2px solid #185FA5' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                          <span style={{ fontSize:13, color:'#555', fontWeight:600 }}>{p.model_no} {p.name}</span>
                          <button
                            onClick={() => { setEditData(p); setFormType('product'); setShowForm(true); setSelectedProd(null); }}
                            style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:6, padding:'6px 14px', fontSize:13, cursor:'pointer', fontWeight:500 }}>
                            ✏️ 수정
                          </button>
                          {canWriteProduct && (
                            <button onClick={() => deleteProduct(p.id)}
                              style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:6, padding:'6px 14px', fontSize:13, cursor:'pointer', fontWeight:500 }}>
                              🗑 삭제
                            </button>
                          )}
                          <button onClick={() => setSelectedProd(null)}
                            style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'6px 12px', fontSize:11, cursor:'pointer', color:'#888' }}>
                            닫기
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          {canWriteProduct && (
            <div style={{ fontSize:12, color:'#aaa', marginTop:8 }}>
              💡 모델번호/품명을 클릭하면 수정·삭제 메뉴가 열려요. 재고 숫자를 클릭하면 수량을 수정할 수 있어요.
            </div>
          )}

          {/* ── 완제품 불량처리 표 ── */}
          <div style={{ marginTop:28 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', tableLayout:'fixed',
              borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
              <thead>
                <tr>
                  <th colSpan={2} style={{ ...thLeft('#f8fafc'), fontSize:14, color:'#dc2626', width:260, minWidth:260, maxWidth:260 }}>완제품 불량처리</th>
                  {/* 상세옵션 열은 재고현황 표와 동일하게 폭을 고정하지 않고 자동배분하며,
                      옵션(=등록된 완제품)이 없는 제품군은 안내 문구 한 칸을 보여준다. */}
                  {optionColumns.length > 0 ? optionColumns.map((c, idx) => (
                    <th key={c} style={{ ...thStyle(getOptionStyle(c, idx).bg, getOptionStyle(c, idx).color), fontSize:14 }}>
                      {getOptionLabel(c)}
                    </th>
                  )) : (
                    <th style={{ ...thStyle('#f8fafc', '#aaa'), fontSize:13, fontWeight:500, fontStyle:'italic' }}>
                      제품옵션을 등록하세요
                    </th>
                  )}
                  {/* "업데이트 이력" 열: 완제품 재고현황 표와 동일한 규칙 — 불량처리 쓰기권한(수정 가능)이나
                      "수정이력 조회" 읽기권한 둘 중 하나만 있어도 열 자체는 보이되, 실제로 등록/삭제하는
                      동작은 여전히 canDefect로만 제어된다. */}
                  {(canDefect || canReadHistory) && <th style={{ ...thStyle('#f8fafc'), width:320, minWidth:320, maxWidth:320, fontSize:13 }}>업데이트 이력</th>}
                </tr>
              </thead>
              <tbody>
                {groupProducts.length === 0 ? (
                  <tr><td colSpan={2+Math.max(optionColumns.length,1)+((canDefect||canReadHistory)?1:0)} style={{ textAlign:'center', padding:'40px', color:'#aaa', fontSize:13 }}>
                    등록된 완제품이 없습니다.
                  </td></tr>
                ) : groupProducts.map((p, i) => {
                  const latest = defectLatestFor(p.id);
                  return (
                  <tr key={'defect-'+p.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td colSpan={2} style={{ ...tdStyle, width:260, minWidth:260, maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <div style={{ fontWeight:700, fontSize:14, color:'#1a1a1a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.model_no} <span style={{ fontWeight:500, color:'#888' }}>({p.name})</span></div>
                    </td>
                    {optionColumns.map(c => {
                      const dq = defectQty(p.id, c);
                      return (
                        <td key={c}
                          style={{ ...tdStyle, textAlign:'center', cursor: canDefect ? 'pointer' : 'default' }}
                          onClick={() => canDefect && setDefectRegister({ product:p, color:c })}>
                          <span style={{ fontSize:15, fontWeight:600, color: dq>0 ? '#dc2626' : '#ccc' }}>
                            {formatNum(dq)}
                          </span>
                        </td>
                      );
                    })}
                    {(canDefect || canReadHistory) && (
                      <td style={{ ...tdStyle, textAlign:'center', width:320, minWidth:320, maxWidth:320, cursor:'pointer' }}
                        onClick={() => setDefectListView({ product:p })}>
                        {latest ? (
                          <span style={{ fontSize:13, whiteSpace:'nowrap' }}>
                            <span style={{ color:'#dc2626', fontWeight:500 }}>{latest.defect_date}</span>
                            <span style={{ color:'#888' }}> · {latest.created_by}</span>
                          </span>
                        ) : (
                          <span style={{ fontSize:13, color:'#ccc' }}>-</span>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize:12, color:'#aaa', marginTop:8 }}>
              💡 불량수량을 클릭하면 불량처리를 등록할 수 있어요. "수정" 칸을 클릭하면 해당 제품의 전체 불량 이력을 볼 수 있어요.
            </div>
          </div>
        </div>
      )}

      {/* ── 부품 탭 ── */}
      {tab === 'part' && canReadParts && (
        <PartTable data={groupParts} isSuperAdmin={isSuperAdmin} canWrite={canWriteParts} canEdit={canEditParts} canReadHistory={canReadHistory}
          columns={stockColumns['부품']}
          selectedPart={selectedPart} onSelectPart={setSelectedPart}
          onEdit={(p) => { setSelectedPart(null); setEditData(p); setFormType('part'); setShowForm(true); }}
          onDelete={(id) => { setSelectedPart(null); deletePart(id); }}
          onStockEdit={(p) => setEditStock({ part:p, qty:p.quantity||0 })}
          onReorder={(ids) => reorderParts(ids, false)}
          onViewHistory={openItemHistory}
          isSupply={false} />
      )}

      {/* ── 소모품 탭 ── */}
      {tab === 'supply' && canReadSupply && (
        <PartTable data={groupSupplies} isSuperAdmin={isSuperAdmin} canWrite={canWriteSupply} canEdit={canEditSupply} canReadHistory={canReadHistory}
          columns={stockColumns['소모품']}
          selectedPart={selectedPart} onSelectPart={setSelectedPart}
          onEdit={(p) => { setSelectedPart(null); setEditData(p); setFormType('supply'); setShowForm(true); }}
          onDelete={(id) => { setSelectedPart(null); deletePart(id); }}
          onStockEdit={(p) => setEditStock({ part:p, qty:p.quantity||0 })}
          onReorder={(ids) => reorderParts(ids, true)}
          onViewHistory={openItemHistory}
          isSupply={true} />
      )}

      {/* ── 원자재 탭(소모품 탭 우측, 구 "자재발주관리") ── */}
      {tab === 'material_order' && canReadMaterialOrder && (
        <MaterialOrderTab
          orders={materialOrders}
          canWrite={canWriteMaterialOrder}
          onReload={loadMaterialOrders}
          showMsg={showMsg}
          onEdit={(o) => { setMaterialEditData(o); setShowMaterialForm(true); }}
          filterCategory={materialFilterCategory}
          search={materialSearch}
          categoryColors={materialCategoryColors}
          columns={stockColumns['원자재_표시열']}
        />
      )}

      {/* ── 자재발주 등록/수정 폼 + 재질별 단가표 관리(헤더 공용 버튼에서 오픈) ── */}
      {showMaterialForm && (
        <MaterialOrderForm
          editData={materialEditData}
          onClose={() => setShowMaterialForm(false)}
          onSaved={() => { setShowMaterialForm(false); loadMaterialOrders(); showMsg(materialEditData ? '수정 완료!' : '등록 완료!'); }}
          categories={materialCategories}
          columns={stockColumns['원자재_표시열']}
        />
      )}
      {showMaterialPriceManager && (
        <MaterialUnitPriceManager onClose={() => setShowMaterialPriceManager(false)} categories={materialCategories}
          onCategoriesChanged={() => loadColumns('원자재')} />
      )}

      {/* ── 제품군 등록/삭제 관리 모달 ── */}
      {showGroupManager && (
        <ProductGroupManager
          groups={groups}
          itemCounts={groupItemCounts}
          onClose={() => setShowGroupManager(false)}
          onSaved={() => { setShowGroupManager(false); loadGroups(); showMsg('제품군 정보가 저장되었습니다!'); }}
        />
      )}

      {/* ── 목록(상세옵션/표시열) 추가·삭제 관리 모달 ── */}
      {showColumnManager && (
        <ColumnManager
          groupName={currentGroupName}
          category={currentCategory}
          items={stockColumns[currentCategory] || []}
          onClose={() => setShowColumnManager(false)}
          onSaved={() => {
            setShowColumnManager(false);
            loadColumns(currentCategory);
            // 이름이 바뀐 항목이 있으면 백엔드가 기존 데이터(완제품 재고수량/부품·소모품 값)도
            // 새 이름으로 함께 옮겨두므로, 그 결과가 화면에 바로 보이도록 해당 목록도 다시 불러온다.
            if (currentCategory === '완제품') { loadProducts(); }
            else if (currentCategory === '부품') { loadParts(); }
            else if (currentCategory === '소모품') { loadSupplies(); }
            else if (currentCategory === '원자재_표시열') { loadMaterialOrders(); }
            showMsg('목록이 저장되었습니다!');
          }}
          onSave={(items, renames) => API.put('/stock/columns', {
            group_id: (currentCategory === '원자재' || currentCategory === '원자재_표시열') ? MATERIAL_COLUMNS_GROUP_ID : groupTab,
            category: currentCategory, items, renames,
          })}
        />
      )}

      {/* ── 품목 등록 폼 ── */}
      {showForm && (
        <ItemForm formType={formType} editData={editData} groupId={groupTab} groupName={currentGroupName} groups={groups}
          columnsMap={stockColumns}
          canWriteProduct={canWriteProduct} canWriteSupply={canWriteSupply} canWriteParts={canWriteParts}
          onClose={() => { setShowForm(false); setEditData(null); setFormType(''); }}
          onSaved={(type) => {
            setShowForm(false); setEditData(null); setFormType('');
            if (type==='product') loadProducts();
            else if (type==='supply') loadSupplies();
            else loadParts();
            showMsg(editData ? '수정 완료!' : '등록 완료!');
          }} />
      )}

      {/* ── 재고 수정 ── */}
      {editStock && (
        <StockEdit editStock={editStock}
          onClose={() => setEditStock(null)}
          onSaved={() => {
            setEditStock(null);
            setTimeout(() => { loadProducts(); loadParts(); loadSupplies(); }, 300);
            showMsg('재고 수정 완료!');
          }} />
      )}

      {/* ── 수정이력 모달 (현재 선택된 제품군 기준으로 필터링된 이력) ── */}
      {showHistory && (
        <HistoryModal
          history={history}
          groupName={currentGroupName}
          onClose={() => setShowHistory(false)}
          onExport={() => exportHistory()}
          canDelete={canDeleteHistory}
          onDeleted={onHistoryDeleted}
        />
      )}

      {/* ── 부품/소모품 개별 항목 수정이력 모달 — 화면 구성은 위 재고 수정이력과 동일하고,
          이 품번(모델번호)/품명에 해당하는 기록만 걸러서 보여줍니다. ── */}
      {itemHistoryTarget && (
        <HistoryModal
          history={itemHistoryRecords}
          groupName={`${currentGroupName} · ${itemHistoryTarget.name}`}
          onClose={() => setItemHistoryTarget(null)}
          onExport={() => exportHistory(itemHistoryRecords, itemHistoryTarget.name)}
          canDelete={canDeleteHistory}
          onDeleted={onHistoryDeleted}
        />
      )}

      {/* ── 불량처리 등록 모달 ── */}
      {defectRegister && (
        <DefectRegisterModal
          product={defectRegister.product} color={defectRegister.color}
          onClose={() => setDefectRegister(null)}
          onSaved={() => {
            setDefectRegister(null);
            loadProducts(); loadDefectSummary();
            showMsg('불량처리 등록 완료! 재고에서 차감되었습니다.');
          }} />
      )}

      {/* ── 불량처리 이력 목록 모달 ── */}
      {defectListView && (
        <DefectListModal
          product={defectListView.product} color={defectListView.color}
          isSuperAdmin={isSuperAdmin} canDefect={canDefect}
          onClose={() => setDefectListView(null)}
          onDeleted={() => { loadProducts(); loadDefectSummary(); showMsg('삭제 완료! 재고가 복구되었습니다.'); }}
        />
      )}
    </div>
  );
}

// ── 불량처리 등록 모달 ─────────────────────────────────
function DefectRegisterModal({ product, color, onClose, onSaved }) {
  const [quantity, setQuantity] = useState('');
  const [content, setContent]   = useState('');
  const [defectDate, setDefectDate] = useState(getKstDateString());
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async () => {
    if (!quantity || Number(quantity) <= 0) return alert('불량수량을 입력해주세요.');
    setLoading(true);
    try {
      await API.post('/stock/defects', {
        product_id: product.id, color, quantity: Number(quantity), content, defect_date: defectDate
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const lbl = { display:'block', fontSize:13, fontWeight:600, marginBottom:6, marginTop:14, color:'#333' };
  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:400, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>⚠️ 불량처리 등록</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ marginTop:10, fontSize:13, color:'#555' }}>
          <b>{product.model_no}</b> ({product.name}) ·{' '}
          <span style={{ padding:'2px 8px', borderRadius:10, fontWeight:600,
            background:getOptionStyle(color).bg, color:getOptionStyle(color).color, fontSize:12 }}>
            {getOptionLabel(color)}
          </span>
        </div>
        <p style={{ fontSize:12, color:'#888', marginTop:6 }}>등록하면 완제품 재고에서 해당 수량만큼 자동 차감돼요.</p>

        <label style={lbl}>일자</label>
        <input type="date" value={defectDate} onChange={e=>setDefectDate(e.target.value)} style={inp} />

        <label style={lbl}>불량수량 *</label>
        <input type="number" value={quantity} onChange={e=>setQuantity(e.target.value)} style={inp} />

        <label style={lbl}>불량내용</label>
        <textarea value={content} onChange={e=>setContent(e.target.value)}
          style={{ ...inp, minHeight:70, resize:'vertical' }} placeholder="불량 사유를 입력해주세요" />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#dc2626', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer', marginTop:20 }}>
          {loading ? '처리 중...' : '불량처리 등록'}
        </button>
      </div>
    </div>
  );
}

// ── 불량처리 이력 목록 모달 ─────────────────────────────────
function DefectListModal({ product, color, isSuperAdmin, canDefect, onClose, onDeleted }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const gridCols = canDefect ? '90px 65px 1fr 60px 90px 1.3fr 60px' : '90px 65px 1fr 60px 90px 1.3fr';

  const load = () => API.get('/stock/defects/list', { params: color ? { product_id:product.id, color } : { product_id:product.id } })
    .then(r => { setLogs(r.data); setLoading(false); }).catch(()=>setLoading(false));

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('이 불량처리 내역을 삭제하시겠습니까? (재고가 자동 복구됩니다)')) return;
    try {
      await API.delete('/stock/defects/'+id);
      load(); onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:680, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>⚠️ 불량처리 이력</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ marginTop:6, marginBottom:16, fontSize:13, color:'#555' }}>
          <b>{product.model_no}</b> ({product.name})
          {color && (
            <>
              {' · '}
              <span style={{ padding:'2px 8px', borderRadius:10, fontWeight:600,
                background:getOptionStyle(color).bg, color:getOptionStyle(color).color, fontSize:12 }}>
                {getOptionLabel(color)}
              </span>
            </>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa' }}>불러오는 중...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px 0', color:'#aaa' }}>불량처리 이력이 없습니다.</div>
        ) : (
          <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns: gridCols,
              padding:'8px 10px', background:'#f8fafc', fontSize:11, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb' }}>
              <span>일자</span><span>색상</span><span>품명</span><span>수량</span><span>불량확인</span><span>불량내용</span>
              {canDefect && <span style={{textAlign:'center'}}>관리</span>}
            </div>
            {logs.map(l => (
              <div key={l.id} style={{ display:'grid', gridTemplateColumns: gridCols,
                padding:'9px 10px', fontSize:12.5, borderBottom:'1px solid #f0f0f0', alignItems:'center' }}>
                <span style={{ color:'#666' }}>{l.defect_date || '-'}</span>
                <span>
                  <span style={{ fontSize:10, padding:'2px 7px', borderRadius:10, fontWeight:600,
                    background:getOptionStyle(l.color).bg, color:getOptionStyle(l.color).color }}>
                    {getOptionLabel(l.color)}
                  </span>
                </span>
                <span>{l.model_no}</span>
                <span style={{ color:'#dc2626', fontWeight:600 }}>{l.quantity}</span>
                <span style={{ color:'#666' }}>{l.created_by || '-'}</span>
                <span style={{ color:'#888' }}>{l.content || '-'}</span>
                {canDefect && (
                  <span style={{ textAlign:'center' }}>
                    <button onClick={() => handleDelete(l.id)}
                      style={{ background:'none', border:'none', cursor:'pointer', fontSize:14 }} title="삭제">🗑️</button>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 수정이력 모달 ─────────────────────────────────
// history는 이미 부모(Inventory)에서 현재 선택된 제품군(groupTab) 기준으로 필터링되어
// 전달됩니다(GET /stock/history?group_id=...). 여기서는 완제품/부품/소모품 구분만 추가로 거릅니다.
function HistoryModal({ history, groupName, onClose, onExport, canDelete = false, onDeleted }) {
  const [filter, setFilter] = useState('전체');
  // 년/월 선택 필터: updated_at이 "2026. 08. 08. 19:02:19" 형식이라 앞 두 자리(연/월)만
  // 정규식으로 뽑아서 비교합니다. 연도는 "전체"를 선택하면 월 선택은 의미가 없어지므로
  // 함께 "전체"로 되돌리고 비활성화합니다.
  const [yearFilter, setYearFilter] = useState('전체');
  const [monthFilter, setMonthFilter] = useState('전체');
  // 삭제할 항목 선택(체크박스) — "수정이력 삭제" 권한이 있을 때만 표시됩니다.
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const parseYM = (updatedAt) => {
    const m = /^(\d{4})\.\s*(\d{2})/.exec(String(updatedAt || ''));
    return m ? { year: m[1], month: m[2] } : null;
  };
  const years = Array.from(new Set(history.map(h => parseYM(h.updated_at)?.year).filter(Boolean))).sort().reverse();

  const filtered = history.filter(h => {
    if (filter !== '전체' && h.category !== filter) return false;
    if (yearFilter !== '전체') {
      const ym = parseYM(h.updated_at);
      if (!ym || ym.year !== yearFilter) return false;
      if (monthFilter !== '전체' && ym.month !== monthFilter) return false;
    }
    return true;
  });

  const CAT_COLOR = { '완제품':'#185FA5', '부품':'#0F6E56', '소모품':'#7C3AED', '원자재':'#B45309' };
  const CAT_BG    = { '완제품':'#E6F1FB', '부품':'#E1F5EE', '소모품':'#EDE9FE', '원자재':'#FEF3C7' };

  const allChecked = filtered.length > 0 && filtered.every(h => selected.has(h.id));
  const toggleAll = () => {
    setSelected(prev => {
      if (allChecked) return new Set();
      const next = new Set(prev);
      filtered.forEach(h => next.add(h.id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const handleDeleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`선택한 ${selected.size}건의 수정이력을 삭제하시겠습니까? 삭제한 이력은 복구할 수 없습니다.`)) return;
    setDeleting(true);
    try {
      await API.post('/stock/history/delete-bulk', { ids: Array.from(selected) });
      setSelected(new Set());
      if (onDeleted) onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const th = { padding:'10px 12px', textAlign:'center', fontWeight:600, color:'#666', whiteSpace:'nowrap' };
  const td = { padding:'10px 12px', textAlign:'center', whiteSpace:'nowrap' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:860, maxWidth:'95vw',
        maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{ padding:'20px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <h3 style={{ fontSize:18, fontWeight:600, margin:0 }}>📋 재고 수정이력{groupName ? ` · ${groupName}` : ''}</h3>
            <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>
              {groupName ? `${groupName} ` : ''}{history.length}건의 수정이력
            </p>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {canDelete && (
              <button onClick={handleDeleteSelected} disabled={!selected.size || deleting}
                style={{ background: selected.size ? '#dc2626' : '#f0f0f0',
                  color: selected.size ? '#fff' : '#aaa', border:'none', borderRadius:8,
                  padding:'8px 16px', fontSize:13, fontWeight:600,
                  cursor: selected.size ? 'pointer' : 'default' }}>
                🗑 선택 삭제{selected.size ? ` (${selected.size})` : ''}
              </button>
            )}
            <button onClick={onExport}
              style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
                padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              📥 엑셀 다운로드
            </button>
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        {/* 필터 탭 */}
        <div style={{ padding:'12px 20px 0', borderBottom:'1px solid #f0f0f0',
          display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', flexShrink:0 }}>
          {['전체','완제품','부품','소모품','원자재'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding:'6px 16px', border:'none', borderRadius:20, cursor:'pointer', fontSize:13, fontWeight:500,
                background: filter===f ? (CAT_BG[f]||'#f0f0f0') : '#f8fafc',
                color:      filter===f ? (CAT_COLOR[f]||'#333')  : '#888',
                marginBottom:8 }}>
              {f} ({f==='전체' ? history.length : history.filter(h=>h.category===f).length})
            </button>
          ))}
          <div style={{ display:'flex', gap:6, alignItems:'center', marginLeft:'auto', marginBottom:8 }}>
            <select value={yearFilter}
              onChange={e => { setYearFilter(e.target.value); setMonthFilter('전체'); }}
              style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, color:'#555', outline:'none' }}>
              <option value="전체">연도 전체</option>
              {years.map(y => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
              disabled={yearFilter === '전체'}
              style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13,
                color: yearFilter === '전체' ? '#ccc' : '#555', outline:'none',
                background: yearFilter === '전체' ? '#f5f5f5' : '#fff' }}>
              <option value="전체">{yearFilter === '전체' ? '월 전체' : `${yearFilter}년 전체`}</option>
              {Array.from({length:12}, (_, i) => String(i+1).padStart(2,'0')).map(m => <option key={m} value={m}>{Number(m)}월</option>)}
            </select>
          </div>
        </div>

        {/* 테이블 */}
        <div style={{ overflowY:'auto', flex:1, padding:'0 20px' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead style={{ position:'sticky', top:0, zIndex:1 }}>
              <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e5e7eb' }}>
                {canDelete && (
                  <th style={{ ...th, width:36 }}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll}
                      style={{ cursor:'pointer' }} />
                  </th>
                )}
                <th style={th}>구분</th>
                <th style={th}>모델번호</th>
                <th style={th}>옵션</th>
                <th style={th}>수정전</th>
                <th style={th}>수정후</th>
                <th style={th}>수정자</th>
                <th style={th}>수정일시</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={canDelete ? 8 : 7} style={{ textAlign:'center', padding:'40px', color:'#aaa' }}>
                  수정이력이 없습니다.
                </td></tr>
              ) : filtered.map((h, i) => {
                const changed = h.after_qty !== null && h.before_qty !== null && h.after_qty !== h.before_qty;
                const increased = h.after_qty > h.before_qty;
                return (
                  <tr key={h.id} style={{ borderBottom:'1px solid #f0f0f0', background: i%2===0?'#fff':'#fafafa' }}>
                    {canDelete && (
                      <td style={td}>
                        <input type="checkbox" checked={selected.has(h.id)} onChange={() => toggleOne(h.id)}
                          style={{ cursor:'pointer' }} />
                      </td>
                    )}
                    <td style={td}>
                      <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:500,
                        background: CAT_BG[h.category]    || '#f0f0f0',
                        color:      CAT_COLOR[h.category] || '#555' }}>
                        {h.category}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight:600, color:'#1a1a1a' }}>{h.model_no || '-'}</td>
                    <td style={{ ...td, color:'#666' }}>{h.color || '-'}</td>
                    <td style={{ ...td, color:'#888' }}>
                      {h.before_qty !== null ? (h.before_qty).toLocaleString() : '-'}
                    </td>
                    <td style={{ ...td, fontWeight:600,
                      color: !changed ? '#888' : increased ? '#16a34a' : '#dc2626' }}>
                      {h.after_qty !== null ? (h.after_qty).toLocaleString() : '-'}
                      {changed && <span style={{ fontSize:11, marginLeft:4 }}>{increased ? '▲' : '▼'}</span>}
                    </td>
                    <td style={{ ...td, color:'#555' }}>{h.updated_by}</td>
                    <td style={{ ...td, color:'#aaa', fontSize:12 }}>{h.updated_at}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 부품/소모품 공통 테이블 ──────────────────────────
// 부품/소모품 탭 "목록"(상단 목록추가/삭제)에 있는 열 이름 중, 실제로 데이터가 있는 항목은
// 그대로 값을 보여주고, 아직 값이 없는 신규 열(재질/공급사 등, 추후 정의 예정)은 "-"로 비워둔다.
const CENTER_COLS = new Set(['No', '재고수량']);
const DEFAULT_PART_COLUMNS = ['No','품번','품명','재질','재고수량','공급사','비고'];

// 부품/소모품 제품등록 폼은 상단 "목록추가/삭제"에서 설정해둔 표시열 목록을 그대로 입력폼으로
// 사용합니다(No는 자동 번호이므로 입력 대상에서 제외). 품번/품명/규격·치수/재고수량/비고처럼
// parts 테이블에 실제 컬럼이 있는 항목은 해당 필드에 바로 매핑하고, 재질/공급사처럼 아직 전용
// 컬럼이 없는 항목(추후 정의 예정)은 비고(note)에 "이름: 값" 형태로 함께 저장합니다.
const PART_FIELD_MAP = { '품번':'part_no', '품명':'name', '규격/치수':'dimension', '재고수량':'quantity', '비고':'note' };
const CATEGORY_OF_TYPE = { part:'부품', supply:'소모품' };

// 부품/소모품 표는 더 이상 끝에 "관리" 열을 따로 두지 않습니다(관리 권한이 있는 경우, 행을
// 클릭하면 바로 아래에 수정/삭제 버튼이 있는 액션 행이 펼쳐지는 방식으로 대체됩니다).
function PartTable({ data, isSuperAdmin, onEdit, onDelete, onStockEdit, isSupply, canWrite = true, canEdit = true, canReadHistory = false, columns, selectedPart, onSelectPart, onReorder, onViewHistory }) {
  const cols = (columns && columns.length)
    ? columns.map(c => (typeof c === 'string' ? { name: c, width_pct: null } : c))
    : DEFAULT_PART_COLUMNS.map(name => ({ name, width_pct: null }));
  // canWrite = 등록/삭제("부품 등록/삭제" · "소모품 등록/삭제") 권한, canEdit = 수정("부품수정" ·
  // "소모품 수정") 권한 — 서로 다른 세부권한이므로 행 선택/재고수량 클릭수정/수정버튼/삭제버튼을
  // 각각 알맞은 권한으로 나눠서 가드합니다.
  const canInteract = canWrite || canEdit;
  // "업데이트 이력" 열: 완제품 재고현황 표와 동일한 규칙 — 쓰기권한(등록/삭제 또는 수정)이나
  // "수정사항 보기" 읽기권한 중 하나만 있어도 열 자체는 보이되, 실제 최근 수정 정보(날짜/작성자)는
  // "수정사항 보기" 권한이 있을 때만 표시된다.
  const showHistoryCol = canInteract || canReadHistory;
  // 드래그 앤 드롭으로 순서를 바꿀 수 있는 손잡이(⠿) 열 — 순서변경은 백엔드에서 등록/삭제와 같은
  // 권한(parts_write/consumables_write)으로 확인하므로 canWrite 기준으로 보여준다.
  const showDragCol = canWrite && !!onReorder && data.length > 1;
  const colSpanAll = cols.length + (showHistoryCol ? 1 : 0) + (showDragCol ? 1 : 0);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const handleDrop = (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setOverIdx(null); return; }
    const reordered = [...data];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setDragIdx(null); setOverIdx(null);
    onReorder(reordered.map(r => r.id));
  };
  // 재질/공급사처럼 parts 테이블에 전용 컬럼이 없는 커스텀 열은 extra_fields(JSON 문자열)에
  // { 열이름: 값 } 형태로 저장되어 있습니다. 여기서 한 번 파싱해서 꺼내 씁니다.
  const getExtraFields = (p) => {
    if (!p || !p.extra_fields) return {};
    try { const parsed = JSON.parse(p.extra_fields); return (parsed && typeof parsed === 'object') ? parsed : {}; }
    catch { return {}; }
  };
  const renderCell = (name, p, idx) => {
    switch (name) {
      case 'No':      return idx + 1;
      case '품번':     return p.part_no || '-';
      case '품명':     return p.name || '-';
      case '규격/치수': return p.dimension || '-';
      case '비고':     return p.note || '-';
      case '재고수량': return null; // 아래에서 클릭 가능한 특수 셀로 별도 렌더링
      default:        return getExtraFields(p)[name] || '-';
    }
  };

  // "목록추가/삭제 > 수정"에서 지정한 width_pct(표 전체 폭 대비 %)가 있는 열은 그 폭을 그대로
  // 쓰고, 지정하지 않은 열은 남은 폭을 균등하게 나눠 가집니다.
  const specifiedTotal = cols.reduce((sum, c) => sum + (c.width_pct != null ? Number(c.width_pct) : 0), 0);
  const unspecifiedCount = cols.filter(c => c.width_pct == null).length;
  const autoWidth = unspecifiedCount > 0 ? Math.max(0, (100 - specifiedTotal) / unspecifiedCount) : 0;
  const widthFor = (c) => c.width_pct != null ? `${c.width_pct}%` : (unspecifiedCount > 0 ? `${autoWidth}%` : undefined);

  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', tableLayout:'fixed',
        borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        <thead>
          <tr>
            {showDragCol && <th style={{ ...thStyle('#f8fafc'), width:28, minWidth:28, maxWidth:28 }}></th>}
            {/* 부품/소모품 표는 완제품 표와 달리 모든 열을 가운데 정렬합니다. */}
            {cols.map(c => (
              <th key={c.name} style={{ ...thStyle('#f8fafc'), textAlign:'center', fontSize:14, width: widthFor(c) }}>
                {c.name}
              </th>
            ))}
            {showHistoryCol && <th style={{ ...thStyle('#f8fafc'), width:240, minWidth:240, maxWidth:240, fontSize:13 }}>업데이트 이력</th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={colSpanAll}
              style={{ textAlign:'center', padding:'40px', color:'#aaa', fontSize:14 }}>
              등록된 항목이 없습니다.
            </td></tr>
          ) : data.map((p, i) => {
            const isSelected = canInteract && selectedPart && selectedPart.id === p.id;
            const isDragOver = showDragCol && overIdx === i && dragIdx !== null && dragIdx !== i;
            const rowBg = isDragOver ? '#dbeafe' : isSelected ? '#eef4fc' : (i%2===0?'#fff':'#fafafa');
            return (
              <Fragment key={p.id}>
                <tr style={{ background: rowBg, cursor: canInteract ? 'pointer' : 'default' }}
                  onMouseEnter={e => { if (!isSelected && !isDragOver) e.currentTarget.style.background='#f0f6ff'; }}
                  onMouseLeave={e => { if (!isSelected && !isDragOver) e.currentTarget.style.background = i%2===0?'#fff':'#fafafa'; }}
                  onClick={() => canInteract && onSelectPart(isSelected ? null : p)}
                  onDragOver={e => { if (showDragCol) { e.preventDefault(); setOverIdx(i); } }}
                  onDrop={e => { if (showDragCol) { e.preventDefault(); handleDrop(i); } }}>
                  {showDragCol && (
                    <td style={{ ...tdStyle, width:28, minWidth:28, maxWidth:28, textAlign:'center', cursor:'grab', color:'#bbb' }}
                      draggable
                      onClick={e => e.stopPropagation()}
                      onDragStart={e => { e.stopPropagation(); setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                      title="드래그해서 순서 변경">
                      ⠿
                    </td>
                  )}
                  {cols.map(c => c.name === '재고수량' ? (
                    <td key={c.name} style={{ ...tdStyle, textAlign:'center', cursor: canEdit?'pointer':'default' }}
                      onClick={e => { if (canEdit) { e.stopPropagation(); onStockEdit(p); } }}>
                      <span style={{ fontSize:14, fontWeight:600, color:(p.quantity||0)>0?'#185FA5':'#ccc' }}>
                        {(p.quantity||0).toLocaleString()}
                      </span>
                    </td>
                  ) : c.name === '불량재고' ? (
                    <td key={c.name} style={{ ...tdStyle, textAlign:'center',
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ fontSize:14, fontWeight:600, color:'#dc2626' }}>
                        {renderCell(c.name, p, i)}
                      </span>
                    </td>
                  ) : (
                    <td key={c.name} style={{ ...tdStyle, textAlign:'center', color:'#666666',
                      fontWeight:400, fontSize:14,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {renderCell(c.name, p, i)}
                    </td>
                  ))}
                  {/* 업데이트 이력: 최근 수정 날짜/작성자는 "수정사항 보기" 권한이 있을 때만 표시 */}
                  {showHistoryCol && (
                    <td style={{ ...tdStyle, textAlign:'center', width:240, minWidth:240, maxWidth:240 }}>
                      {canReadHistory && p.updated_at ? (
                        <span style={{ fontSize:13, whiteSpace:'nowrap' }}>
                          <span style={{ color:'#185FA5', fontWeight:600, fontSize:14 }}>{p.updated_at}</span>
                          <span style={{ color:'#888' }}> · {p.updated_by}</span>
                        </span>
                      ) : (
                        <span style={{ fontSize:13, color:'#ccc' }}>-</span>
                      )}
                    </td>
                  )}
                </tr>
                {isSelected && (
                  <tr style={{ background:'#eef4fc' }}>
                    <td colSpan={colSpanAll} style={{ padding:'6px 14px 10px', textAlign:'right', borderBottom:'1px solid #dbeafe' }}>
                      <div style={{ display:'inline-flex', gap:6 }}>
                        {onViewHistory && (canInteract || canReadHistory) && (
                          <button onClick={e => { e.stopPropagation(); onViewHistory(p); }}
                            style={{ background:'#fff', color:'#7C3AED', border:'1px solid #7C3AED', borderRadius:6,
                              padding:'4px 10px', fontSize:11, cursor:'pointer', fontWeight:600 }}>
                            📋 수정이력 조회
                          </button>
                        )}
                        {canEdit && <button onClick={e => { e.stopPropagation(); onEdit(p); }} style={smallBtn('#185FA5')}>수정</button>}
                        {canWrite && <button onClick={e => { e.stopPropagation(); onDelete(p.id); }} style={smallBtn('#dc2626')}>삭제</button>}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── 품목 등록/수정 폼 ──────────────────────────────
// groupId: 지금 화면에서 선택 중인 제품군(안전벨트/안전블럭 등)의 id — 신규 등록 시 이
// 제품군으로 품목이 태그되어, 그 제품군 탭에서 바로 보이게 된다(수정 시에는 기존 제품군을
// 그대로 유지하므로 사용하지 않음).
function ItemForm({ formType, editData, groupId, groupName = '', groups = [], columnsMap = {}, onClose, onSaved, canWriteProduct = true, canWriteSupply = true, canWriteParts = true }) {
  const isEdit = !!editData;
  const [type, setType] = useState(
    formType || (editData
      ? (editData.model_no !== undefined ? 'product' : editData.category === '소모품' ? 'supply' : 'part')
      : '')
  );
  // 신규 등록 시 어느 제품군(안전벨트/안전블럭 등)에 담을지 선택하는 값. 기본값은 지금
  // 화면에서 보고 있던 제품군(groupId)이며, 수정 시에는 기존 제품군을 그대로 유지하므로
  // 이 선택창 자체를 보여주지 않습니다.
  const [formGroupId, setFormGroupId] = useState(groupId);
  const [form, setForm] = useState({
    model_no:  editData?.model_no  || '',
    part_no:   editData?.part_no   || '',
    name:      editData?.name      || '',
    dimension: editData?.dimension || '',
    quantity:  editData?.quantity  || 0,
    note:      editData?.note      || '',
  });
  const [loading, setLoading] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // ── 완제품 신규 등록: 제품번호/제품명/비고만 직접 입력받습니다. 상세옵션은 더 이상 제품마다
  // 따로 입력하지 않고, 재고현황 상단 "목록 추가/삭제"에서 이 제품군에 설정해둔 목록을 그대로
  // 물려받습니다(백엔드에서 처리).

  // ── 부품/소모품 등록/수정 화면은 상단 "목록추가/삭제"에서 이 구분(부품/소모품)에 설정해둔
  // 표시열 목록을 그대로 입력 필드로 구성합니다(No 제외). 재질/공급사처럼 아직 전용 DB 컬럼이
  // 없는 항목은 customFields에 담아 extra_fields(JSON)로 저장하고, 표에서도 그 값을 그대로
  // 해당 열에 표시합니다(예전엔 비고에 텍스트로만 접어넣어서 표의 해당 열에는 반영되지 않는
  // 문제가 있었습니다).
  const partCategory = CATEGORY_OF_TYPE[type];
  // partColumns: 이 목록의 각 항목을 {name, formula, ...} 원본 그대로 보존합니다(계산식 여부를
  // 판단하려면 이름뿐 아니라 목록추가/삭제에서 저장해둔 formula 정보도 필요하기 때문).
  const partColumns = (() => {
    const list = (columnsMap[partCategory] || []).filter(c => c.name !== 'No');
    return list.length ? list : DEFAULT_PART_COLUMNS.filter(n => n !== 'No').map(name => ({ name }));
  })();
  const partColumnNames = partColumns.map(c => c.name);
  const columnByName = Object.fromEntries(partColumns.map(c => [c.name, c]));
  const customColumnNames = partColumnNames.filter(n => !PART_FIELD_MAP[n]);
  const [customFields, setCustomFields] = useState(() => {
    if (editData?.extra_fields) {
      try { const parsed = JSON.parse(editData.extra_fields); if (parsed && typeof parsed === 'object') return parsed; }
      catch { /* 무시 */ }
    }
    return {};
  });
  const updateCustomField = (name, val) => setCustomFields(prev => ({ ...prev, [name]: val }));

  // ── 계산식: 목록추가/삭제에서 항목별로 미리 지정해둔 계산식({operands, operators})을 그대로
  // 따릅니다. 계산식이 지정된 항목은 여기서 직접 입력하지 않고, 계산대상으로 선택된 다른
  // 항목들의 값을 연산자대로 계산해서 자동으로 채웁니다.
  const isFormulaField = (name) => !!(columnByName[name]?.formula);
  // PART_FIELD_MAP에 매핑된 항목(품번/품명/규격치수/재고수량/비고)은 form의 값을, 그 외
  // 커스텀 항목은 customFields의 값을 그대로 원본값으로 취급합니다.
  const getRawFieldValue = (name) => {
    const mapped = PART_FIELD_MAP[name];
    if (mapped) return form[mapped];
    return customFields[name];
  };
  // 계산식을 왼쪽부터 순서대로 계산하면서, 마지막으로 실행된 연산이 나눗셈이었는지와 그때의
  // 두 피연산자(a, b)를 함께 기록해둡니다 — 나눗셈 결과가 소수점으로 떨어질 때 "몫 / 나머지"
  // 형태로 표현하려면 최종 결과값만으로는 부족하고, 그 나눗셈 단계의 원래 a, b가 필요합니다.
  const computeFormulaResult = (name) => {
    const formula = columnByName[name]?.formula;
    if (!formula || !Array.isArray(formula.operands) || formula.operands.length < 2) return null;
    const vals = formula.operands.map(n => Number(getRawFieldValue(n)));
    if (vals.some(v => Number.isNaN(v))) return null;
    let result = vals[0];
    const operators = formula.operators || [];
    let lastDivPair = null;
    for (let i = 0; i < operators.length && i < vals.length - 1; i++) {
      const a = result;
      const b = vals[i + 1];
      if (operators[i] === '+') result = a + b;
      else if (operators[i] === '-') result = a - b;
      else if (operators[i] === '/') {
        if (b === 0) return null;
        result = a / b;
        lastDivPair = { a, b };
      } else result = a * b; // 'x'
      if (operators[i] !== '/') lastDivPair = null; // 마지막 연산이 나눗셈일 때만 의미가 있습니다.
    }
    return { value: result, lastDivPair };
  };
  const computeFormulaValue = (name) => {
    const r = computeFormulaResult(name);
    return r ? r.value : null;
  };
  // 화면에 보여줄 계산 결과 문자열입니다. 나눗셈으로 끝나 소수점이 생기는 경우
  // "몫 / 나머지" 형태(예: 13÷2 → "6 / 1")로, 그 외에는 보통의 숫자로 표시합니다.
  const formatFormulaDisplay = (name) => {
    const r = computeFormulaResult(name);
    if (!r) return null;
    const { value, lastDivPair } = r;
    if (lastDivPair && !Number.isInteger(value)) {
      const { a, b } = lastDivPair;
      const quotient = Math.floor(a / b);
      const remainder = a - quotient * b;
      return `${quotient} / ${remainder}`;
    }
    return Number(value).toLocaleString();
  };
  // 계산식이 지정된 항목이면 계산값(없으면 0/빈값)을, 아니면 원본 입력값을 반환합니다 —
  // 저장 시 실제로 서버에 보낼 값을 최종 결정할 때 사용합니다. 재고수량은 서버에서 숫자로
  // 파싱되므로 원래의 숫자 결과를 그대로 쓰고, 그 외 텍스트 항목(품번/규격치수/비고, 커스텀
  // 항목)은 소수점일 때 "몫 / 나머지" 형태로 보기 좋게 표현한 문자열을 그대로 저장합니다.
  const resolveFieldValue = (name) => {
    if (!isFormulaField(name)) return getRawFieldValue(name);
    if (PART_FIELD_MAP[name] === 'quantity') {
      const computed = computeFormulaValue(name);
      return computed !== null ? computed : 0;
    }
    const formatted = formatFormulaDisplay(name);
    return formatted !== null ? formatted : '';
  };
  const buildExtraFields = () => {
    const obj = {};
    customColumnNames.forEach(name => {
      const v = resolveFieldValue(name);
      const trimmed = (v === null || v === undefined) ? '' : String(v).trim();
      if (trimmed) obj[name] = trimmed;
    });
    return obj;
  };

  const handleSubmit = async () => {
    if (type === 'product' && !isEdit) {
      if (!form.model_no.trim()) { alert('제품번호를 입력해주세요.'); return; }
      if (!form.name.trim())     { alert('제품명을 입력해주세요.'); return; }
      setLoading(true);
      try {
        // options를 보내지 않으면 백엔드가 이 제품군의 "목록"(완제품 상세옵션)을 그대로 사용한다.
        await API.post('/stock/products', {
          model_no: form.model_no, name: form.name, note: form.note,
          group_id: formGroupId
        });
        onSaved('product');
      } catch (err) { alert(err.response?.data?.message || '오류 발생'); }
      finally { setLoading(false); }
      return;
    }

    // 부품/소모품의 품번/규격치수/재고수량/비고는 "계산식" 체크박스가 켜져있으면 직접 입력한
    // 값 대신 나머지 항목들을 곱한 계산값을 그대로 서버에 보냅니다.
    const finalName = (type === 'part' || type === 'supply') ? resolveFieldValue('품명') : form.name;
    if (!finalName) { alert('품명을 입력해주세요.'); return; }

    if ((type === 'part' || type === 'supply') && !isEdit) {
      setLoading(true);
      try {
        const endpoint = type === 'supply' ? '/stock/supplies' : '/stock/parts';
        await API.post(endpoint, {
          part_no: resolveFieldValue('품번') || '', name: finalName,
          dimension: resolveFieldValue('규격/치수') || '', quantity: Number(resolveFieldValue('재고수량')) || 0,
          note: resolveFieldValue('비고') || '',
          extra_fields: buildExtraFields(),
          group_id: formGroupId
        });
        onSaved(type);
      } catch (err) { alert(err.response?.data?.message || '오류 발생'); }
      finally { setLoading(false); }
      return;
    }

    setLoading(true);
    try {
      if (type === 'product') {
        // 완제품 수정: 기존과 동일하게 모델번호/품명/비고를 직접 수정합니다(카탈로그 재선택 없음).
        await API.put('/stock/products/' + editData.id, form);
        onSaved('product');
      } else {
        const resolvedBody = {
          part_no: resolveFieldValue('품번') || '', name: finalName,
          dimension: resolveFieldValue('규격/치수') || '', quantity: Number(resolveFieldValue('재고수량')) || 0,
          note: resolveFieldValue('비고') || '',
          extra_fields: buildExtraFields(),
        };
        if (type === 'supply') {
          await API.put('/stock/parts/' + editData.id, { ...resolvedBody, category:'소모품' });
          onSaved('supply');
        } else {
          await API.put('/stock/parts/' + editData.id, resolvedBody);
          onSaved('part');
        }
      }
    } catch(err) { alert(err.response?.data?.message || '오류 발생'); }
    finally { setLoading(false); }
  };

  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:4 };
  const TYPE_OPTIONS = [
    { val:'product', label:'🏷️ 완제품', desc:'색상별 재고 관리', disabled: !canWriteProduct },
    { val:'part',    label:'🔩 부품',   desc:'수량 재고 관리', disabled: !canWriteParts },
    { val:'supply',  label:'🧹 소모품', desc:'소모품 재고 관리', disabled: !canWriteSupply },
  ];
  // 신규 등록 화면에서는 지금 선택 중인(또는 상단에서 바꾼) 제품군 이름과 선택된 품목구분을
  // 모달 제목에 함께 보여줍니다("제품등록 · 안전벨트 · 부품" 형태).
  const selectedGroupName = groups.find(g => g.id === formGroupId)?.name || groupName;
  const TYPE_LABELS = { product:'완제품', part:'부품', supply:'소모품' };
  const modalTitle = !isEdit
    ? `제품등록${selectedGroupName ? ' · ' + selectedGroupName : ''}${type ? ' · ' + TYPE_LABELS[type] : ''}`
    : '품목 수정';

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{modalTitle}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        {!isEdit && (
          <>
            <label style={lbl}>품목 구분 선택 *</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:16 }}>
              {TYPE_OPTIONS.map(t => (
                <button key={t.val} onClick={() => !t.disabled && setType(t.val)} disabled={t.disabled}
                  style={{ padding:'12px 8px', border: type===t.val?'2px solid #185FA5':'1.5px solid #ddd',
                    borderRadius:10, cursor: t.disabled ? 'not-allowed' : 'pointer', textAlign:'center',
                    opacity: t.disabled ? 0.4 : 1,
                    background: type===t.val?'#E6F1FB':'#fff', color: type===t.val?'#185FA5':'#555' }}>
                  <div style={{ fontSize:14, marginBottom:4 }}>{t.label}</div>
                  <div style={{ fontSize:10, color: type===t.val?'#185FA5':'#aaa' }}>{t.disabled ? '권한없음' : t.desc}</div>
                </button>
              ))}
            </div>
            {groups.length > 0 && (
              <>
                <label style={lbl}>제품군 *</label>
                <select value={formGroupId || ''} onChange={e => setFormGroupId(Number(e.target.value))} style={inp}>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </>
            )}
          </>
        )}
        {type === 'product' && isEdit && (
          <>
            <label style={lbl}>모델번호 *</label>
            <input name="model_no" placeholder="예: NS2602-SD" value={form.model_no} onChange={handleChange} style={inp} />
            <label style={lbl}>품명 *</label>
            <input name="name" placeholder="예: 안전-더블" value={form.name} onChange={handleChange} style={inp} />
            <label style={lbl}>비고</label>
            <input name="note" placeholder="특이사항" value={form.note} onChange={handleChange} style={inp} />
          </>
        )}
        {type === 'product' && !isEdit && (
          <>
            <label style={lbl}>제품번호 *</label>
            <input name="model_no" placeholder="예: NS2602-SD" value={form.model_no} onChange={handleChange} style={inp} />

            <label style={lbl}>제품명 *</label>
            <input name="name" placeholder="예: 안전-더블" value={form.name} onChange={handleChange} style={inp} />

            <div style={{ fontSize:12, color:'#888', background:'#f8fafc', borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
              💡 상세옵션(색상 등)은 상단 "목록추가/삭제"에서 이 제품군에 설정해둔 목록을 그대로 사용합니다.
            </div>

            <label style={lbl}>비고</label>
            <input name="note" placeholder="특이사항" value={form.note} onChange={handleChange} style={inp} />
          </>
        )}
        {(type === 'part' || type === 'supply') && (
          <>
            <div style={{ fontSize:12, color:'#888', background:'#f8fafc', borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
              💡 아래 입력 항목은 상단 "목록추가/삭제"에서 {partCategory}에 설정해둔 목록을 그대로 따릅니다.
            </div>
            {(() => {
              // 항목(라벨+입력창) 하나를 렌더링합니다. 예전엔 이 내용을 바로 <div key=...>로 감싸서
              // 한 줄에 하나씩만 나왔는데, 재질+공급사 / QT'Y·SET+재고수량 / 완제+불량재고처럼 관련
              // 있는 두 항목은 같은 줄에 나란히 보여주기 위해 "내용만" 만드는 함수로 분리했습니다.
              const renderField = (name) => {
                const mapped = PART_FIELD_MAP[name];
                const isCalc = isFormulaField(name);
                const calcDisplay = isCalc ? formatFormulaDisplay(name) : null;
                const calcBadge = isCalc && (
                  <span style={{ marginLeft:8, fontSize:11, fontWeight:400, color:'#185FA5' }}>
                    🧮 계산식 ({columnByName[name].formula.operands.join(
                      columnByName[name].formula.operators?.[0] === '+' ? ' + ' :
                      columnByName[name].formula.operators?.[0] === '-' ? ' - ' :
                      columnByName[name].formula.operators?.[0] === '/' ? ' ÷ ' : ' × '
                    )})
                  </span>
                );
                const calcOutput = (
                  <div style={{ ...inp, display:'flex', alignItems:'center', background:'#f5f5f5',
                    color: calcDisplay !== null ? '#185FA5' : '#aaa', fontWeight:400, fontSize:11 }}>
                    {calcDisplay !== null ? calcDisplay : '계산대상 항목에 숫자를 먼저 입력해주세요'}
                  </div>
                );
                if (mapped === 'quantity') {
                  return (
                    <>
                      <label style={lbl}>{name}{calcBadge}</label>
                      {isCalc ? calcOutput : (
                        <input name="quantity" type="number" value={form.quantity} onChange={handleChange} style={inp} />
                      )}
                    </>
                  );
                }
                if (mapped) {
                  return (
                    <>
                      <label style={lbl}>{name}{name === '품명' && !isCalc ? ' *' : ''}{calcBadge}</label>
                      {isCalc ? calcOutput : (
                        <input name={mapped} value={form[mapped]} onChange={handleChange}
                          placeholder={name === '품번' ? '예: NS0026-P001' : name === '규격/치수' ? '예: 10.3T x 61W x 83.5L' : name === '비고' ? '특이사항' : ''}
                          style={inp} />
                      )}
                    </>
                  );
                }
                return (
                  <>
                    <label style={lbl}>{name}{calcBadge}</label>
                    {isCalc ? calcOutput : (
                      <input value={customFields[name] || ''} onChange={e => updateCustomField(name, e.target.value)}
                        placeholder={`${name} 입력`} style={inp} />
                    )}
                  </>
                );
              };

              // 두 항목이 이 순서로 연달아 나오면 한 줄(2열)에 같이 보여줍니다.
              const PAIR_GROUPS = [["재질", "공급사"], ["QT'Y/SET", "재고수량"], ["완제", "불량재고"]];
              const rows = [];
              for (let i = 0; i < partColumnNames.length; i++) {
                const cur = partColumnNames[i];
                const next = partColumnNames[i + 1];
                const pair = PAIR_GROUPS.find(([a, b]) => a === cur && b === next);
                if (pair) { rows.push([cur, next]); i += 1; }
                else { rows.push([cur]); }
              }

              return rows.map(row => row.length === 2 ? (
                <div key={row.join('-')} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>{renderField(row[0])}</div>
                  <div>{renderField(row[1])}</div>
                </div>
              ) : (
                <div key={row[0]}>{renderField(row[0])}</div>
              ));
            })()}
          </>
        )}
        {type && (
          <button onClick={handleSubmit} disabled={loading}
            style={{ width:'100%', padding:'12px', background: loading?'#aaa':'#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:15, fontWeight:600,
              cursor: loading?'not-allowed':'pointer', marginTop:4 }}>
            {loading ? '저장 중...' : (type === 'product' && !isEdit ? '품목등록' : (isEdit ? '수정 완료' : '등록 완료'))}
          </button>
        )}
      </div>
    </div>
  );
}

// ── 제품군 등록/삭제 관리 모달 ─────────────────────────
// 재고현황 상단의 안전벨트/안전블럭 같은 "제품군" 탭을 관리합니다. 이름 추가/수정/삭제와
// 순서변경(▲▼ 버튼, 곧 좌측 탭 순서에 그대로 반영)을 모두 화면에서 자유롭게 편집한 뒤,
// "저장" 버튼을 눌러야만 실제로 서버에 반영됩니다("취소"를 누르면 아무 것도 바뀌지 않음).
function ProductGroupManager({ groups, itemCounts = {}, onClose, onSaved }) {
  const [items, setItems] = useState(() => groups.map(g => ({ key: 'g' + g.id, id: g.id, name: g.name })));
  const [deletedIds, setDeletedIds] = useState([]);
  const [newName, setNewName] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  const originalMap = new Map(groups.map(g => [g.id, g.name]));

  const addItem = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setItems([...items, { key: 'new-' + Date.now() + '-' + Math.random(), id: null, name: trimmed }]);
    setNewName('');
  };

  const startEdit = (item) => { setEditingKey(item.key); setEditingName(item.name); };
  const confirmEdit = () => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setItems(items.map(it => it.key === editingKey ? { ...it, name: trimmed } : it));
    setEditingKey(null); setEditingName('');
  };
  const cancelEdit = () => { setEditingKey(null); setEditingName(''); };

  const removeItem = (item) => {
    // 이미 저장되어 있는 제품군은, 그 안에 등록된 품목(완제품/부품/소모품)이 하나라도 있으면
    // 서버에서도 삭제를 막는다. 저장을 눌러보고 나서야 알기보다, 여기서 미리 알려주고 막는다.
    if (item.id) {
      const count = itemCounts[item.id] || 0;
      if (count > 0) {
        alert(`"${item.name}"에 등록된 품목이 ${count}개 있어 삭제할 수 없습니다. 먼저 품목을 삭제하거나 다른 제품군으로 옮겨주세요.`);
        return;
      }
      setDeletedIds(prev => [...prev, item.id]);
    }
    setItems(items.filter(it => it.key !== item.key));
    if (editingKey === item.key) { setEditingKey(null); setEditingName(''); }
  };

  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
  };

  const handleSave = async () => {
    if (!items.length) { alert('제품군은 1개 이상 있어야 합니다.'); return; }
    setSaving(true);
    try {
      // 1) 삭제 예약된 제품군 먼저 제거
      for (const id of deletedIds) {
        await API.delete('/stock/groups/' + id);
      }
      // 2) 신규 추가된 제품군은 생성, 이름이 바뀐 기존 제품군은 수정
      const finalIds = [];
      for (const item of items) {
        if (!item.id) {
          const res = await API.post('/stock/groups', { name: item.name });
          finalIds.push(res.data.id);
        } else {
          if (originalMap.get(item.id) !== item.name) {
            await API.put('/stock/groups/' + item.id, { name: item.name });
          }
          finalIds.push(item.id);
        }
      }
      // 3) 화면에 보이는 순서 그대로 정렬순서 반영
      await API.put('/stock/groups/reorder', { order: finalIds });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally { setSaving(false); }
  };

  const inp = { flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:440, maxWidth:'92vw', maxHeight:'88vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>🏷️ 제품군 등록/삭제</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12, color:'#888', margin:'6px 0 16px' }}>
          안전벨트 / 안전블럭처럼 재고현황 상단에 표시되는 제품군 탭을 관리합니다. 순서를 바꾸면 좌측 탭 순서도 함께 바뀌며, "저장"을 눌러야 실제로 반영됩니다.
        </p>

        <label style={{ fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:6 }}>새 제품군 이름</label>
        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          <input value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem()}
            placeholder="예: 안전모, 안전화" style={inp} />
          <button onClick={addItem} disabled={!newName.trim()}
            style={{ padding:'0 16px', border:'none', borderRadius:8, cursor: newName.trim() ? 'pointer' : 'not-allowed',
              background: newName.trim() ? '#185FA5' : '#ccc', color:'#fff', fontSize:13, fontWeight:600 }}>
            추가
          </button>
        </div>

        <div style={{ fontSize:13, color:'#555', fontWeight:500, marginBottom:6 }}>등록된 제품군 ({items.length})</div>
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden', flex:1, overflowY:'auto', marginBottom:20, minHeight:60 }}>
          {items.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#aaa', fontSize:13 }}>등록된 제품군이 없습니다.</div>
          ) : items.map((item, idx) => (
            <div key={item.key} style={{ display:'flex', alignItems:'center', gap:6,
              padding:'8px 10px', borderBottom: idx === items.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
              <div style={{ display:'flex', flexDirection:'column' }}>
                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} title="위로"
                  style={{ border:'none', background:'none', cursor: idx === 0 ? 'default' : 'pointer',
                    color: idx === 0 ? '#ddd' : '#888', fontSize:11, lineHeight:1, padding:'1px 4px' }}>▲</button>
                <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} title="아래로"
                  style={{ border:'none', background:'none', cursor: idx === items.length - 1 ? 'default' : 'pointer',
                    color: idx === items.length - 1 ? '#ddd' : '#888', fontSize:11, lineHeight:1, padding:'1px 4px' }}>▼</button>
              </div>
              {editingKey === item.key ? (
                <>
                  <input value={editingName} onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && confirmEdit()} autoFocus
                    style={{ flex:1, padding:'6px 10px', border:'1px solid #185FA5', borderRadius:6, fontSize:13, outline:'none' }} />
                  <button onClick={confirmEdit} style={smallBtn('#185FA5')}>확인</button>
                  <button onClick={cancelEdit}
                    style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer', color:'#888' }}>취소</button>
                </>
              ) : (
                <>
                  <span style={{ flex:1, fontSize:13, fontWeight:500, color:'#1a1a1a' }}>
                    {item.name}
                    {!item.id && <span style={{ fontSize:10, marginLeft:6, color:'#16a34a', fontWeight:600 }}>NEW</span>}
                    {item.id && (
                      <span style={{ fontSize:11, marginLeft:6, color: (itemCounts[item.id]||0) > 0 ? '#888' : '#bbb' }}>
                        (품목 {itemCounts[item.id] || 0}개)
                      </span>
                    )}
                  </span>
                  <button onClick={() => startEdit(item)} style={smallBtn('#185FA5')}>수정</button>
                  <button onClick={() => removeItem(item)}
                    disabled={item.id && (itemCounts[item.id] || 0) > 0}
                    title={item.id && (itemCounts[item.id] || 0) > 0 ? '이 제품군에 등록된 품목이 있어 삭제할 수 없습니다' : undefined}
                    style={{ ...smallBtn('#dc2626'),
                      ...(item.id && (itemCounts[item.id] || 0) > 0
                        ? { background:'#f3a3a3', cursor:'not-allowed' }
                        : {}) }}>
                    삭제
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'12px', background: saving ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
              fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose} disabled={saving}
            style={{ flex:1, padding:'12px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 목록(상세옵션/표시열) 추가·삭제 관리 모달 ─────────────────────
// 완제품 탭에서는 이 목록이 검정/파랑/빨강처럼 그 제품군의 모든 완제품이 공통으로 갖는
// 상세옵션이 되고, 부품/소모품 탭에서는 표에 보여줄 열 이름(No/품번/품명/재질/재고수량/
// 공급사/비고 등)이 됩니다. items는 아직 한 번도 저장한 적이 없으면 서버가 내려준 기본값이
// 미리 채워진 상태로 열립니다. 추가/삭제/순서변경/이름변경 모두 "저장"을 눌러야 반영됩니다.
function ColumnManager({ groupName, category, items, onClose, onSaved, onSave, priceRows, onEditPriceRow, onDeletePriceRow }) {
  // 2026-09(원자재 탭 목록관리 재설계) — '원자재' 카테고리는 이제 표시열이 아니라 구분값(자재/부품)
  // 색상 관리 전용(재질별 단가표 관리 모달의 "구분 관리" 버튼)이므로, 완제품과 동일하게
  // 배경색/글자색 지정 UI(폭%·계산식 없음)를 사용한다.
  const isProduct = category === '완제품' || category === '원자재';
  // originalName: 이 항목이 원래(저장 전) 어떤 이름으로 불렸는지 기억해둡니다. 완제품의 실제
  // 재고수량(product_stock.color)이나 부품/소모품의 커스텀 값(parts.extra_fields)은 이 "목록"
  // 항목의 이름을 그대로 키로 써서 저장되어 있어서, 이름만 바꾸고 그 연결을 안 챙기면 기존
  // 데이터가 옛 이름 밑에 그대로 남아 화면에는 "반영이 안 된" 것처럼(값이 사라진 것처럼) 보입니다.
  // 그래서 "저장" 시 originalName과 최종 name이 다른 항목만 골라 renames로 함께 보내서, 백엔드가
  // 기존 데이터도 새 이름으로 따라가게(cascade) 합니다. 새로 추가한 항목은 originalName이 없으므로
  // (=이전에 저장된 적 없음) rename 대상이 아닙니다.
  const [rows, setRows] = useState(() => items.map((it, i) => ({
    key: 'c' + i + '-' + it.name,
    name: it.name,
    originalName: it.name,
    bg_color: it.bg_color || null,
    text_color: it.text_color || null,
    width_pct: it.width_pct != null ? it.width_pct : null,
    formula: (it.formula && Array.isArray(it.formula.operands) && it.formula.operands.length >= 2) ? it.formula : null,
  })));
  const [newName, setNewName] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingBg, setEditingBg] = useState('#334155');
  const [editingText, setEditingText] = useState('#ffffff');
  const [editingWidth, setEditingWidth] = useState('');
  // 계산식: 다른 목록 항목(최대 3개)을 계산대상으로 선택하고, 그 사이에 연산자(+ - x ÷)를
  // 지정합니다. editingOperands[i]가 비어있으면 그 이후 칸은 화면에 보이지 않습니다.
  const [editingOperands, setEditingOperands] = useState(['', '', '']);
  const [editingOperators, setEditingOperators] = useState(['x', 'x']);
  const [saving, setSaving] = useState(false);

  const addRow = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    // "마지막에 입력한 목록이 2번 보이는" 버그 방지 — 추가 버튼 더블클릭이나 Enter+클릭이
    // 겹쳐 눌렸을 때 같은 이름의 행이 두 번 들어가지 않도록, 이미 목록에 있는 이름이면
    // 무시합니다(대소문자/공백 차이 없이 완전히 같은 이름일 때만 막습니다).
    if (rows.some(r => r.name === trimmed)) { setNewName(''); return; }
    // 기본값: 배경 #f8fafc / 글자 #666666 (배경과 글자를 같은 색으로 두면 글자가 안 보이므로,
    // 나중에 정리된 "기본값" 스펙(font-color #666666)을 그대로 새 항목에도 적용합니다).
    setRows([...rows, { key: 'new-' + Date.now() + '-' + Math.random(), name: trimmed, originalName: null, bg_color:'#f8fafc', text_color:'#666666', width_pct:null, formula:null }]);
    setNewName('');
  };
  const startEdit = (row) => {
    setEditingKey(row.key);
    setEditingName(row.name);
    setEditingBg(row.bg_color || baseOptionStyle(row.name).bg);
    setEditingText(row.text_color || baseOptionStyle(row.name).color);
    setEditingWidth(row.width_pct != null ? String(row.width_pct) : '');
    const ops = (row.formula && row.formula.operands) || [];
    setEditingOperands([ops[0] || '', ops[1] || '', ops[2] || '']);
    const oprs = (row.formula && row.formula.operators) || [];
    setEditingOperators([oprs[0] || 'x', oprs[1] || 'x']);
  };
  const confirmEdit = () => {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setRows(rows.map(r => {
      if (r.key !== editingKey) return r;
      if (isProduct) return { ...r, name: trimmed, bg_color: editingBg || null, text_color: editingText || null };
      const w = editingWidth === '' ? null : Number(editingWidth);
      const compactOperands = editingOperands.filter(v => v);
      const formula = compactOperands.length >= 2
        ? { operands: compactOperands, operators: editingOperators.slice(0, compactOperands.length - 1) }
        : null;
      return { ...r, name: trimmed, width_pct: (w !== null && !Number.isNaN(w)) ? w : null, formula };
    }));
    setEditingKey(null); setEditingName('');
  };
  const resetColor = () => { setEditingBg(baseOptionStyle(editingName).bg); setEditingText(baseOptionStyle(editingName).color); };
  const clearFormula = () => { setEditingOperands(['', '', '']); setEditingOperators(['x', 'x']); };
  const cancelEdit = () => { setEditingKey(null); setEditingName(''); };
  const removeRow = (row) => {
    setRows(rows.filter(r => r.key !== row.key));
    if (editingKey === row.key) { setEditingKey(null); setEditingName(''); }
  };
  const moveRow = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next);
  };

  const totalWidthPct = rows.reduce((sum, r) => sum + (r.width_pct != null ? Number(r.width_pct) : 0), 0);

  const handleSave = async () => {
    // 목록 항목이 0개(전부 삭제)여도 저장할 수 있어야 하므로 최소 1개 이상 조건은 두지 않는다.
    setSaving(true);
    try {
      // originalName(저장 전 이름)이 있고, 최종 이름과 달라진 항목만 "이름이 바뀐 항목"으로 골라
      // 함께 보냅니다 — 백엔드가 이 목록을 보고 기존 데이터(완제품 재고수량/부품·소모품 값)도
      // 옛 이름에서 새 이름으로 따라가도록 옮겨줍니다.
      const renames = rows
        .filter(r => r.originalName && r.originalName !== r.name)
        .map(r => ({ from: r.originalName, to: r.name }));
      await onSave(
        rows.map(r => ({ name: r.name, bg_color: r.bg_color, text_color: r.text_color, width_pct: r.width_pct, formula: r.formula })),
        renames
      );
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally { setSaving(false); }
  };

  const inp = { flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', outline:'none' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'92vw', maxHeight:'88vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>
            {category === '원자재' ? '구분 관리' : isProduct ? '제품옵션 추가/삭제' : '목록 추가/삭제'} · {category}{groupName ? ` · ${groupName}` : ''}
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12, color:'#888', margin:'6px 0 16px' }}>
          {category === '원자재'
            ? '자재발주 등록시 선택하는 "구분" 값 목록입니다(예: 자재, 부품). "수정"에서 항목별 배경색/글자색을 직접 지정할 수 있습니다.'
            : isProduct
            ? '이 제품군의 모든 완제품이 공통으로 갖는 상세옵션(색상 등) 목록입니다. "수정"에서 항목별 배경색/글자색을 직접 지정할 수 있습니다.'
            : '표에 보여줄 열 이름 목록입니다. "수정"에서 항목별 폭 비율(%)과, 다른 항목 값으로 자동 계산되는 계산식을 지정할 수 있습니다.'}
          {' '}순서를 바꾸면 화면 표시 순서도 함께 바뀌며, "저장"을 눌러야 실제로 반영됩니다.
        </p>

        <label style={{ fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:6 }}>새 항목 이름</label>
        <div style={{ display:'flex', gap:6, marginBottom:16 }}>
          <input value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addRow()}
            placeholder={category === '원자재' ? '예: 사급자재' : isProduct ? '예: 네이비' : '예: 재질'} style={inp} />
          <button onClick={addRow} disabled={!newName.trim()}
            style={{ padding:'0 16px', border:'none', borderRadius:8, cursor: newName.trim() ? 'pointer' : 'not-allowed',
              background: newName.trim() ? '#185FA5' : '#ccc', color:'#fff', fontSize:13, fontWeight:600 }}>
            추가
          </button>
        </div>

        <div style={{ fontSize:13, color:'#555', fontWeight:500, marginBottom:6 }}>
          목록 ({rows.length}){!isProduct && totalWidthPct > 0 && (
            <span style={{ marginLeft:8, fontSize:12, fontWeight:400, color: totalWidthPct > 100 ? '#dc2626' : '#888' }}>
              지정된 폭 합계 {totalWidthPct}%{totalWidthPct > 100 ? ' (100%를 초과했습니다)' : ''}
            </span>
          )}
        </div>
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden', flex:1, overflowY:'auto', marginBottom:20, minHeight:60 }}>
          {rows.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'#aaa', fontSize:13 }}>
              {isProduct ? '등록된 목록이 없습니다.' : '등록된 항목이 없습니다.'}
            </div>
          ) : rows.map((row, idx) => (
            <div key={row.key} style={{ borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 10px' }}>
                <div style={{ display:'flex', flexDirection:'column' }}>
                  <button onClick={() => moveRow(idx, -1)} disabled={idx === 0} title="위로"
                    style={{ border:'none', background:'none', cursor: idx === 0 ? 'default' : 'pointer',
                      color: idx === 0 ? '#ddd' : '#888', fontSize:11, lineHeight:1, padding:'1px 4px' }}>▲</button>
                  <button onClick={() => moveRow(idx, 1)} disabled={idx === rows.length - 1} title="아래로"
                    style={{ border:'none', background:'none', cursor: idx === rows.length - 1 ? 'default' : 'pointer',
                      color: idx === rows.length - 1 ? '#ddd' : '#888', fontSize:11, lineHeight:1, padding:'1px 4px' }}>▼</button>
                </div>
                {editingKey === row.key ? (
                  <input value={editingName} onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && confirmEdit()} autoFocus
                    style={{ flex:1, padding:'6px 10px', border:'1px solid #185FA5', borderRadius:6, fontSize:13, outline:'none' }} />
                ) : isProduct ? (
                  <span style={{ flex:1, fontSize:13, fontWeight:500 }}>
                    {/* 저장 전이라도 "확인"으로 방금 지정한 배경색/글자색이 바로 보이도록, 전역
                        오버라이드(getOptionStyle)보다 이 모달 안의 로컬 row 값을 우선 사용합니다. */}
                    <span style={{ padding:'3px 10px', borderRadius:20, fontWeight:600, fontSize:14,
                      background: row.bg_color || getOptionStyle(row.name).bg,
                      color: row.text_color || getOptionStyle(row.name).color }}>
                      {getOptionLabel(row.name)}
                    </span>
                  </span>
                ) : (
                  <span style={{ flex:1, fontSize:13, fontWeight:500, color:'#1a1a1a' }}>
                    {row.name}
                    {row.formula && <span style={{ marginLeft:6, fontSize:10, color:'#185FA5', fontWeight:600 }}>🧮 계산식</span>}
                  </span>
                )}
                {/* 폭 비율(%) 입력: "수정" 글자 바로 왼쪽에 위치하며, 이 행을 수정 중일 때만
                    활성화됩니다(평소엔 저장된 값을 회색으로 보여주기만 하고 편집은 막습니다). */}
                {!isProduct && (
                  <input type="number" min="0" max="100"
                    value={editingKey === row.key ? editingWidth : (row.width_pct != null ? row.width_pct : '')}
                    onChange={e => editingKey === row.key && setEditingWidth(e.target.value)}
                    disabled={editingKey !== row.key}
                    placeholder="자동" title="폭 비율(%)"
                    style={{ width:52, padding:'4px 6px', border:'1px solid #ddd', borderRadius:6, fontSize:12,
                      textAlign:'center', outline:'none',
                      background: editingKey === row.key ? '#fff' : '#f5f5f5',
                      color: editingKey === row.key ? '#1a1a1a' : '#aaa' }} />
                )}
                {editingKey === row.key ? (
                  <>
                    <button onClick={confirmEdit} style={smallBtn('#185FA5')}>확인</button>
                    <button onClick={cancelEdit}
                      style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer', color:'#888' }}>취소</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(row)} style={smallBtn('#185FA5')}>수정</button>
                    <button onClick={() => removeRow(row)} style={smallBtn('#dc2626')}>삭제</button>
                  </>
                )}
              </div>
              {editingKey === row.key && isProduct && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'2px 10px 12px 34px' }}>
                  <button onClick={resetColor}
                    style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'3px 8px', fontSize:11, cursor:'pointer', color:'#888', whiteSpace:'nowrap' }}>
                    기본값
                  </button>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#666' }}>
                    배경색
                    <input type="color" value={editingBg} onChange={e => setEditingBg(e.target.value)}
                      style={{ width:26, height:24, border:'1px solid #ddd', borderRadius:4, padding:0, cursor:'pointer', flexShrink:0 }} />
                    {/* 네이티브 색상 선택기는 브라우저(OS)가 그리는 팝업이라 표기법(HEX/RGB)이나
                        확인 버튼을 우리 코드에서 제어할 수 없습니다. 그래서 항상 HEX 문자열을
                        직접 확인/입력할 수 있는 텍스트 입력을 함께 두고, 값 확정은 이 행의
                        "확인" 버튼으로 하도록 했습니다. */}
                    <input type="text" value={editingBg}
                      onChange={e => { const v = e.target.value.toUpperCase(); if (/^#[0-9A-F]{0,6}$/.test(v)) setEditingBg(v); }}
                      onKeyDown={e => e.key === 'Enter' && confirmEdit()}
                      placeholder="#RRGGBB" maxLength={7}
                      style={{ width:60, padding:'4px 4px', border:'1px solid #ddd', borderRadius:6, fontSize:11,
                        fontFamily:'monospace', textAlign:'center', outline:'none', color:'#333' }} />
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#666' }}>
                    글자색
                    <input type="color" value={editingText} onChange={e => setEditingText(e.target.value)}
                      style={{ width:26, height:24, border:'1px solid #ddd', borderRadius:4, padding:0, cursor:'pointer', flexShrink:0 }} />
                    <input type="text" value={editingText}
                      onChange={e => { const v = e.target.value.toUpperCase(); if (/^#[0-9A-F]{0,6}$/.test(v)) setEditingText(v); }}
                      onKeyDown={e => e.key === 'Enter' && confirmEdit()}
                      placeholder="#RRGGBB" maxLength={7}
                      style={{ width:60, padding:'4px 4px', border:'1px solid #ddd', borderRadius:6, fontSize:11,
                        fontFamily:'monospace', textAlign:'center', outline:'none', color:'#333' }} />
                  </label>
                  <span style={{ padding:'3px 10px', borderRadius:20, fontWeight:600, fontSize:14, background:editingBg, color:editingText, whiteSpace:'nowrap' }}>
                    미리보기
                  </span>
                </div>
              )}
              {editingKey === row.key && !isProduct && (() => {
                const selectSm = { padding:'5px 8px', border:'1px solid #ddd', borderRadius:6, fontSize:12, outline:'none', background:'#fff' };
                const opSm = { ...selectSm, width:46, textAlign:'center' };
                const otherNames = (exclude) => rows.filter(r => r.key !== editingKey && !exclude.includes(r.name)).map(r => r.name);
                return (
                  <div style={{ padding:'2px 10px 14px 34px' }}>
                    <div style={{ fontSize:12, color:'#666', fontWeight:500, marginBottom:6 }}>
                      🧮 계산식 (다른 항목 값으로 자동 계산 — 계산대상 최대 3개)
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      <select value={editingOperands[0]} onChange={e => setEditingOperands([e.target.value, '', ''])} style={selectSm}>
                        <option value="">계산대상 선택</option>
                        {otherNames([]).map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      {editingOperands[0] && (
                        <>
                          <select value={editingOperators[0]} onChange={e => setEditingOperators([e.target.value, editingOperators[1]])} style={opSm}>
                            <option value="+">+</option>
                            <option value="-">-</option>
                            <option value="x">×</option>
                            <option value="/">÷</option>
                          </select>
                          <select value={editingOperands[1]} onChange={e => setEditingOperands([editingOperands[0], e.target.value, ''])} style={selectSm}>
                            <option value="">계산대상 선택</option>
                            {otherNames([editingOperands[0]]).map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </>
                      )}
                      {editingOperands[0] && editingOperands[1] && (
                        <>
                          <select value={editingOperators[1]} onChange={e => setEditingOperators([editingOperators[0], e.target.value])} style={opSm}>
                            <option value="+">+</option>
                            <option value="-">-</option>
                            <option value="x">×</option>
                            <option value="/">÷</option>
                          </select>
                          <select value={editingOperands[2]} onChange={e => setEditingOperands([editingOperands[0], editingOperands[1], e.target.value])} style={selectSm}>
                            <option value="">계산대상 선택</option>
                            {otherNames([editingOperands[0], editingOperands[1]]).map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </>
                      )}
                      {editingOperands[0] && (
                        <button type="button" onClick={clearFormula}
                          style={{ background:'none', border:'1px solid #ddd', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer', color:'#888' }}>
                          지우기
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize:11, color:'#aaa', marginTop:6 }}>
                      이 항목은 제품등록 화면에서 직접 입력하지 않고, 선택한 계산대상 값을 계산해 자동으로 채웁니다.
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>

        {/* 2026-10 요청: "단가표 관리버튼에서 구분관리를 누르면 현재 등록된 구분/재질/두께범위/
            적용년월/단가/비고도 목록에 나타나고 수정이 가능해야 함" — 구분 관리는 자재/부품 같은
            "구분" 라벨만 관리하던 모달인데, 실제 단가표(재질별 단가) 항목도 여기서 바로 확인하고
            수정으로 넘어갈 수 있게 전체 목록을 함께 보여준다. "수정"을 누르면 이 모달을 닫고
            상위 단가표 관리 화면의 등록폼에 해당 행을 채워 바로 수정할 수 있도록 한다. */}
        {category === '원자재' && priceRows && (
          <div style={{ marginBottom:20, borderTop:'1px solid #eee', paddingTop:16 }}>
            <div style={{ fontSize:13, color:'#555', fontWeight:500, marginBottom:8 }}>
              등록된 단가 목록 ({priceRows.length})
            </div>
            <div style={{ border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden', maxHeight:220, overflowY:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['구분','재질','두께범위','적용년월','단가','비고',''].map(h => (
                      <th key={h} style={{ padding:'6px 8px', background:'#f8fafc', fontSize:11, color:'#666', borderBottom:'1px solid #eee', textAlign:'center' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {priceRows.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign:'center', color:'#999', padding:16, fontSize:12 }}>등록된 단가가 없습니다.</td></tr>
                  )}
                  {priceRows.map(r => (
                    <tr key={r.id}>
                      <td style={{ padding:'6px 8px', textAlign:'center', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>{r.category||'-'}</td>
                      <td style={{ padding:'6px 8px', textAlign:'center', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>{r.material}</td>
                      <td style={{ padding:'6px 8px', textAlign:'center', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>
                        {(r.thickness_min||r.thickness_max) ? `${r.thickness_min||0}~${r.thickness_max||''}` : '-'}
                      </td>
                      <td style={{ padding:'6px 8px', textAlign:'center', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>{r.ym||'-'}</td>
                      <td style={{ padding:'6px 8px', textAlign:'right', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>{formatNum(r.unit_price)}</td>
                      <td style={{ padding:'6px 8px', textAlign:'center', fontSize:12, borderBottom:'1px solid #f4f4f4' }}>{r.note||'-'}</td>
                      <td style={{ padding:'6px 8px', textAlign:'center', borderBottom:'1px solid #f4f4f4', whiteSpace:'nowrap' }}>
                        <button onClick={() => onEditPriceRow && onEditPriceRow(r)} style={smallBtn('#185FA5')}>수정</button>{' '}
                        <button onClick={() => onDeletePriceRow && onDeletePriceRow(r.id)} style={smallBtn('#dc2626')}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex:1, padding:'12px', background: saving ? '#aaa' : '#185FA5', color:'#fff', border:'none', borderRadius:8,
              fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose} disabled={saving}
            style={{ flex:1, padding:'12px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8,
              fontSize:14, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 재고 수정 모달 ────────────────────────────────
function StockEdit({ editStock, onClose, onSaved }) {
  const isProduct = !!editStock.product;
  const [qty, setQty]         = useState(editStock.qty || 0);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (isProduct) {
        await API.put('/stock/products/' + editStock.product.id + '/stock', {
          color: editStock.color, quantity: Number(qty)
        });
      } else {
        await API.patch('/stock/parts/' + editStock.part.id + '/quantity', {
          quantity: Number(qty)
        });
      }
      setTimeout(() => { onSaved(); }, 300);
    } catch(err) {
      alert(err.response?.data?.message || '오류 발생');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:340, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>재고 수정</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ background:'#f8fafc', borderRadius:8, padding:'12px 14px', marginBottom:16, fontSize:13 }}>
          {isProduct ? (
            <>
              <div style={{ fontWeight:600 }}>{editStock.product.model_no} {editStock.product.name}</div>
              <div style={{ marginTop:6 }}>
                <span style={{ padding:'3px 12px', borderRadius:20, fontWeight:600, fontSize:12,
                  background: getOptionStyle(editStock.color).bg,
                  color:      getOptionStyle(editStock.color).color }}>
                  {getOptionLabel(editStock.color)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight:600 }}>{editStock.part.name}</div>
              {editStock.part.part_no && <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{editStock.part.part_no}</div>}
            </>
          )}
        </div>
        <label style={{ fontSize:13, color:'#555', fontWeight:500, display:'block', marginBottom:6 }}>수량</label>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
          <button onClick={() => setQty(q => Math.max(0, Number(q)-1))}
            style={{ width:36, height:36, border:'1px solid #ddd', borderRadius:8, background:'#f0f0f0', fontSize:18, cursor:'pointer' }}>-</button>
          <input type="number" value={qty} onChange={e => setQty(e.target.value)}
            style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:16, textAlign:'center', fontWeight:600, outline:'none' }} />
          <button onClick={() => setQty(q => Number(q)+1)}
            style={{ width:36, height:36, border:'1px solid #ddd', borderRadius:8, background:'#f0f0f0', fontSize:18, cursor:'pointer' }}>+</button>
        </div>
        <button onClick={handleSave} disabled={loading}
          style={{ width:'100%', padding:'11px', background: loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, fontSize:14, fontWeight:600,
            cursor: loading?'not-allowed':'pointer' }}>
          {loading ? '저장 중...' : '재고 저장'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// 자재발주관리 — 재고관리(소모품 탭 우측) 신규 탭. 금형/지그 제작용 원자재(강재 등,
// SPEC 가로/세로/두께 + 재질 기반 자재비 산정)와 외주가공 부품의 발주를 등록·관리한다.
// 사용자가 업로드한 "발주 내역 통합관리" 엑셀의 데이터 구조(SPEC/재질/공정/단가표)를
// 참고하여, 목록 + 등록/수정 폼 + 재질별 단가표 관리 3가지로 구성했다.
// ════════════════════════════════════════════════════════════════════════
// 2026-09(원자재 탭 목록관리 재설계) — 목록추가/삭제(ColumnManager)에서 아직 한 번도 저장한
// 적이 없을 때(비어있을 때) 쓰는 기본 표시열 목록. 기존에 화면에 하드코딩되어 있던 13개 열
// 이름을 그대로 가져왔다.
const DEFAULT_MATERIAL_COLUMNS = ['구분','발주일','프로젝트','품번/지그모델명','공정','SPEC(가로×세로×두께)','재질','수량','단가','자재비','발주처','입고예정일','상태'];

function MaterialOrderTab({ orders, canWrite, onReload, showMsg, onEdit, filterCategory, search, categoryColors, columns }) {
  // 2026-09(원자재 탭 개편) — 등록/수정 폼과 단가표 모달은 이제 Inventory 상위 컴포넌트가
  // 헤더의 공용 버튼(+ 자재발주 / 💲 단가표 관리)에서 열어주므로, 이 컴포넌트는 목록 표시만
  // 담당한다(행 클릭 시 상위에서 받은 onEdit으로 수정 모달을 연다). 전체/자재/부품 필터와 검색어도
  // 상위(Inventory)의 헤더 영역(목록추가/삭제 왼쪽)에서 조작하며 이 컴포넌트는 props로 전달받는다.
  // 2026-09(목록관리 재설계) — 표시열이 더 이상 하드코딩이 아니라 목록추가/삭제(ColumnManager)가
  // 관리하는 stockColumns['원자재_표시열']을 그대로 받아 부품/소모품 표(PartTable)와 동일한
  // 방식으로 열을 구성한다(순서 변경/표시 여부/폭(%) 지정 + 완전히 새로운 열 추가까지 가능).
  // 2026-09(목록 UI개선) — 컬럼명을 클릭하면 오름차순/내림차순으로 정렬한다.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const byCategory = filterCategory === '전체' ? orders : orders.filter(o => o.category === filterCategory);
  const keyword = (search || '').trim().toLowerCase();
  const filtered = !keyword ? byCategory : byCategory.filter(o => {
    const haystack = [o.vehicle_model, o.part_no, o.process, o.material, o.vendor, o.in_status, o.note]
      .filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(keyword);
  });

  const CELL = { ...tdStyle, fontSize:14 };

  const cols = (columns && columns.length)
    ? columns.map(c => (typeof c === 'string' ? { name: c, width_pct: null } : c))
    : DEFAULT_MATERIAL_COLUMNS.map(name => ({ name, width_pct: null }));

  // SPEC/재질처럼 material_orders에 전용 컬럼이 있는 표준 13개 열이 아닌, 목록추가/삭제에서
  // 완전히 새로 추가한 커스텀 열은 parts.extra_fields와 동일하게 material_orders.extra_fields
  // (JSON 문자열)에 { 열이름: 값 } 형태로 저장되어 있습니다.
  const getExtraFields = (o) => {
    if (!o || !o.extra_fields) return {};
    try { const parsed = JSON.parse(o.extra_fields); return (parsed && typeof parsed === 'object') ? parsed : {}; }
    catch { return {}; }
  };

  // '프로젝트'/'품번/지그모델명'/'SPEC(가로×세로×두께)'은 신규 표준명이지만, 이전에 저장된
  // 목록(stock_columns)이나 구버전 기본값('차종'/'품번/모델'/'상세사양')을 그대로 쓰는 설치본도
  // 있을 수 있어 두 이름 모두 같은 내부 필드로 인식하도록 별칭을 함께 등록합니다.
  const KNOWN_FIELD_MAP = {
    '구분':'category', '발주일':'order_date',
    '프로젝트':'vehicle_model', '차종':'vehicle_model',
    '품번/지그모델명':'part_no', '품번/모델':'part_no',
    '공정':'process', '재질':'material', '발주처':'vendor', '입고예정일':'expected_date', '상태':'in_status',
  };
  const SPEC_COL_NAMES = ['SPEC(가로×세로×두께)', '상세사양'];

  const getSortValue = (o, name) => {
    if (name === '수량') return Number(o.qty) || 0;
    if (name === '단가') return Number(o.unit_price) || 0;
    if (name === '자재비') return Number(o.material_cost) || 0;
    if (SPEC_COL_NAMES.includes(name)) return Number(o.thickness_mm) || 0;
    if (KNOWN_FIELD_MAP[name]) return (o[KNOWN_FIELD_MAP[name]] ?? '').toString();
    return (getExtraFields(o)[name] || '').toString();
  };

  const handleSort = (name) => {
    if (sortKey === name) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(name); setSortDir('asc'); }
  };

  let sorted = filtered;
  if (sortKey) {
    sorted = [...filtered].sort((a, b) => {
      const va = getSortValue(a, sortKey), vb = getSortValue(b, sortKey);
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb), 'ko') : String(vb).localeCompare(String(va), 'ko');
    });
  }

  const handleDelete = (id) => {
    if (!window.confirm('이 발주 내역을 삭제하시겠습니까?')) return;
    API.delete('/stock/material-orders/' + id)
      .then(() => { onReload(); showMsg('삭제되었습니다.'); })
      .catch(() => showMsg('삭제 중 오류가 발생했습니다.'));
  };

  // 2026-09 신규(요구사항 3,6) — 자재발주 등록시 자동생성된 발주서(엑셀/PDF) 다운로드 +
  // 회계관리(발주관리→매입관리)에서의 결재(이체확인증) 상태를 "미결재"/"결재완료(YYMMDD)"로 표시.
  // GET /material-orders 조회 시 함께 내려주는 pay_status/pay_date(백엔드 JOIN)를 그대로 읽는다.
  const handleDownloadPo = (o) => {
    API.get(`/stock/material-orders/${o.id}/po-download`, { responseType: 'blob' })
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = o.po_file_name || `발주서_${o.po_no || o.id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => showMsg('발주서 파일을 찾을 수 없습니다.'));
  };
  const payLabel = (o) => {
    if (o.pay_status === '지급완료' && o.pay_date) {
      const ymd = String(o.pay_date).replace(/-/g, '');
      return `결재완료(${ymd.slice(2)})`;
    }
    return '미결재';
  };

  // "목록추가/삭제 > 수정"에서 지정한 width_pct(표 전체 폭 대비 %)가 있는 열은 그 폭을 그대로
  // 쓰고, 지정하지 않은 열(과 항상 존재하는 관리열 1칸)은 남은 폭을 균등하게 나눠 가집니다.
  const specifiedTotal = cols.reduce((sum, c) => sum + (c.width_pct != null ? Number(c.width_pct) : 0), 0);
  const unspecifiedCount = cols.filter(c => c.width_pct == null).length + (canWrite ? 1 : 0);
  const autoWidth = unspecifiedCount > 0 ? Math.max(0, (100 - specifiedTotal) / unspecifiedCount) : 0;
  const widthFor = (c) => (c && c.width_pct != null) ? `${c.width_pct}%` : (unspecifiedCount > 0 ? `${autoWidth}%` : undefined);

  const renderCell = (name, o) => {
    switch (name) {
      case '구분':
        return (
          <span style={{ padding:'2px 8px', borderRadius:6, fontSize:12, fontWeight:600,
            background: (categoryColors && categoryColors[o.category] && categoryColors[o.category].bg) || '#E6F1FB',
            color: (categoryColors && categoryColors[o.category] && categoryColors[o.category].color) || '#185FA5' }}>
            {o.category}
          </span>
        );
      case '발주일': return o.order_date || '-';
      case '프로젝트': case '차종': return o.vehicle_model || '-';
      case '품번/지그모델명': case '품번/모델': return o.part_no || '-';
      case '공정': return o.process || '-';
      case 'SPEC(가로×세로×두께)': case '상세사양':
        return (o.width_mm||o.height_mm||o.thickness_mm)
          ? `${o.width_mm||'-'} × ${o.height_mm||'-'} × ${o.thickness_mm||'-'}`
          : '-';
      case '재질': return o.material || '-';
      case '수량': return formatNum(o.qty);
      case '단가': return formatNum(o.unit_price);
      case '자재비': return <span style={{ fontWeight:600 }}>{formatNum(o.material_cost)}</span>;
      case '발주처': return o.vendor || '-';
      case '입고예정일': return o.expected_date || '-';
      case '상태':
        return (
          <span style={{ padding:'2px 8px', borderRadius:6, fontSize:12, fontWeight:600,
            background: o.in_status==='입고완료' ? '#dcfce7' : o.in_status==='취소' ? '#fee2e2' : '#fef9c3',
            color: o.in_status==='입고완료' ? '#166534' : o.in_status==='취소' ? '#991b1b' : '#854d0e' }}>
            {o.in_status || '발주'}
          </span>
        );
      default:
        return getExtraFields(o)[name] || '-';
    }
  };

  const alignFor = (name) => (name === '단가' || name === '자재비') ? 'right' : 'center';

  return (
    <div>
      <div style={{ overflowX:'auto', border:'1px solid #eee', borderRadius:10 }}>
        <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.name} onClick={() => handleSort(c.name)}
                  style={{ ...thStyle(), fontSize:14, cursor:'pointer', userSelect:'none', width: widthFor(c) }}>
                  {c.name}
                  {sortKey === c.name && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
              <th style={{ ...thStyle(), fontSize:14, width:90 }}>발주서</th>
              <th style={{ ...thStyle(), fontSize:14, width:110 }}>결재</th>
              {canWrite && <th style={{ ...thStyle(), fontSize:14, width: widthFor(null) }}></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={cols.length + 2 + (canWrite ? 1 : 0)} style={{ ...CELL, textAlign:'center', color:'#999', padding:30 }}>등록된 발주 내역이 없습니다.</td></tr>
            )}
            {sorted.map(o => (
              <tr key={o.id} style={{ cursor: canWrite ? 'pointer' : 'default' }}
                onClick={() => canWrite && onEdit && onEdit(o)}>
                {cols.map(c => (
                  <td key={c.name} style={{ ...CELL, textAlign: alignFor(c.name),
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {renderCell(c.name, o)}
                  </td>
                ))}
                <td style={{ ...CELL, textAlign:'center' }}>
                  {o.po_file_name || o.po_no ? (
                    <button onClick={(e) => { e.stopPropagation(); handleDownloadPo(o); }}
                      style={smallBtn('#185FA5')}>다운로드</button>
                  ) : '-'}
                </td>
                <td style={{ ...CELL, textAlign:'center' }}>
                  <span style={{ padding:'2px 8px', borderRadius:6, fontSize:12, fontWeight:600,
                    background: o.pay_status === '지급완료' ? '#dcfce7' : '#f1f5f9',
                    color: o.pay_status === '지급완료' ? '#166534' : '#64748b' }}>
                    {payLabel(o)}
                  </span>
                </td>
                {canWrite && (
                  <td style={{ ...CELL, textAlign:'center' }}>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(o.id); }}
                      style={smallBtn('#dc2626')}>삭제</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 자재발주 등록/수정 폼 ────────────────────────────────────────────────
function MaterialOrderForm({ editData, onClose, onSaved, categories, columns }) {
  const isEdit = !!editData;
  const categoryOptions = (categories && categories.length) ? categories : ['자재', '부품'];
  // 2026-09(목록관리 재설계) — 목록추가/삭제에서 표준 13개 열에 없는 완전히 새로운 열을 추가한
  // 경우, 부품/소모품(ItemForm)과 동일하게 이 등록폼 하단에 자유 입력란을 추가로 보여주고
  // extra_fields(JSON)로 저장한다.
  const cols = (columns && columns.length) ? columns.map(c => (typeof c === 'string' ? { name: c } : c)) : [];
  // 2026-09 신규(요구사항 1) — "목록추가/삭제"에서 표시열을 구성하면 이 등록/수정 모달의 표준
  // 입력필드도 그 목록을 따라 자동으로 보이거나 숨겨진다. 목록추가/삭제를 아직 한 번도 쓰지
  // 않았다면(빈 목록) 기존과 동일하게 표준 13개 열을 전부 보여준다(DEFAULT_MATERIAL_COLUMNS).
  const effectiveColNames = cols.length ? cols.map(c => c.name) : DEFAULT_MATERIAL_COLUMNS;
  const hasCol = (name) => effectiveColNames.includes(name);
  // 품번/모델(part_no) 열과 SPEC(가로×세로×두께) 열은 예전 설치에서 이미 "품번/모델"·"상세사양" 같은
  // 다른 이름으로 저장돼 있을 수 있다(이 앱은 목록추가/삭제로 열 이름을 자유롭게 바꿀 수 있어서,
  // 표준 이름이 바뀌어도 이미 저장된 목록 이름은 자동으로 따라오지 않는다). 실제 목록에 있는 이름이
  // 무엇이든(신규 이름/구 이름 모두) part_no·SPEC 입력을 정확한 자리에 렌더링하고, 화면에는 실제
  // 저장된 이름을 그대로 라벨로 보여준다.
  const resolveCol = (...candidates) => candidates.find(name => hasCol(name)) || null;
  const partNoCol = resolveCol('품번/지그모델명', '품번/모델');
  const specCol = resolveCol('SPEC(가로×세로×두께)', '상세사양');
  const customColumnNames = cols.map(c => c.name)
    .filter(n => !DEFAULT_MATERIAL_COLUMNS.includes(n) && n !== partNoCol && n !== specCol);
  const [customFields, setCustomFields] = useState(() => {
    if (editData?.extra_fields) {
      try { const parsed = JSON.parse(editData.extra_fields); if (parsed && typeof parsed === 'object') return parsed; }
      catch { /* 무시 */ }
    }
    return {};
  });
  const updateCustomField = (name, val) => setCustomFields(prev => ({ ...prev, [name]: val }));
  const [form, setForm] = useState(() => ({
    category: editData?.category || categoryOptions[0],
    order_date: editData?.order_date || getKstDateString(),
    vehicle_model: editData?.vehicle_model || '',
    part_no: editData?.part_no || '',
    process: editData?.process || '',
    width_mm: editData?.width_mm ?? '',
    height_mm: editData?.height_mm ?? '',
    thickness_mm: editData?.thickness_mm ?? '',
    material: editData?.material || '',
    qty: editData?.qty ?? 1,
    unit_price: editData?.unit_price ?? 0,
    vendor: editData?.vendor || '',
    expected_date: editData?.expected_date || '',
    in_status: editData?.in_status || '발주',
    in_date: editData?.in_date || '',
    note: editData?.note || '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // 2026-09 신규(요구사항 2-4) — 발주처(외주업체 등) 입력을 영업관리 > 업체관리(companies)에
  // 등록된 회사명으로 자동완성한다. 출고등록/견적서에서 고객사 자동완성에 쓰는 전용 조회
  // 라우트(GET /companies/customers-lookup)를 그대로 재사용하되, 원자재 발주처는 보통
  // 협력사(공급업체)로 등록되므로 ?category=협력사 를 넘겨 협력사만 가져온다(권한 게이트 없이
  // 로그인만 하면 호출 가능한 라우트라 재고관리 메뉴 권한만 있어도 문제 없다).
  const [vendorCompanies, setVendorCompanies] = useState([]);
  useEffect(() => {
    API.get('/companies/customers-lookup', { params: { category: '협력사' } })
      .then(r => setVendorCompanies(r.data || [])).catch(() => {});
  }, []);

  // 재질/두께를 입력하면 단가표에서 자동으로 단가를 조회해 채워준다(직접 수정도 가능).
  const lookupPrice = () => {
    if (!form.material) return;
    API.get('/stock/material-unit-prices/lookup', { params: { material: form.material, thickness: form.thickness_mm || 0, category: form.category } })
      .then(r => { if (r.data && r.data.unit_price) set('unit_price', r.data.unit_price); })
      .catch(() => {});
  };

  const materialCost = (Number(form.qty)||0) * (Number(form.unit_price)||0);

  const submit = () => {
    if (!form.order_date) { alert('발주일은 필수입니다.'); return; }
    setSaving(true);
    const body = { ...form, material_cost: materialCost, extra_fields: customFields };
    const req = isEdit
      ? API.put('/stock/material-orders/' + editData.id, body)
      : API.post('/stock/material-orders', body);
    req.then(onSaved).catch(() => { setSaving(false); alert('저장 중 오류가 발생했습니다.'); });
  };

  const inputStyle = { width:'100%', padding:'9px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box' };
  const labelStyle = { fontSize:12, color:'#666', fontWeight:600, display:'block', marginBottom:4 };
  // 2026-09 신규(요구사항 1) — 필드 수가 목록 구성에 따라 0~2개로 달라질 수 있으므로, 고정
  // 2/3분할 대신 auto-fit 그리드를 써서 보이는 필드 수에 맞춰 자연스럽게 배치되게 한다.
  const row2 = { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:12 };
  const row3 = { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginBottom:12 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:560, maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin:'0 0 16px', fontSize:16, fontWeight:700 }}>{isEdit ? '자재발주 수정' : '자재발주 등록'}</h3>

        {(hasCol('구분') || hasCol('발주일')) && (
          <div style={row2}>
            {hasCol('구분') && (
              <div>
                <label style={labelStyle}>구분</label>
                <select value={form.category} onChange={e => set('category', e.target.value)} style={inputStyle}>
                  {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {hasCol('발주일') && (
              <div>
                <label style={labelStyle}>발주일 *</label>
                <input type="date" value={form.order_date} onChange={e => set('order_date', e.target.value)} style={inputStyle} />
              </div>
            )}
          </div>
        )}

        {(partNoCol || hasCol('프로젝트')) && (
          <div style={row2}>
            {partNoCol && (
              <div>
                <label style={labelStyle}>{partNoCol}</label>
                <input value={form.part_no} onChange={e => set('part_no', e.target.value)} style={inputStyle} />
              </div>
            )}
            {hasCol('프로젝트') && (
              <div>
                <label style={labelStyle}>프로젝트</label>
                <input value={form.vehicle_model} onChange={e => set('vehicle_model', e.target.value)} style={inputStyle} placeholder="예: CN7" />
              </div>
            )}
          </div>
        )}

        {(hasCol('공정') || hasCol('재질')) && (
          <div style={row2}>
            {hasCol('공정') && (
              <div>
                <label style={labelStyle}>공정</label>
                <input value={form.process} onChange={e => set('process', e.target.value)} style={inputStyle} placeholder="예: OP10" />
              </div>
            )}
            {hasCol('재질') && (
              <div>
                <label style={labelStyle}>재질</label>
                <input value={form.material} onChange={e => set('material', e.target.value)} onBlur={lookupPrice} style={inputStyle} />
              </div>
            )}
          </div>
        )}

        {(hasCol('수량') || hasCol('단가') || hasCol('자재비')) && (
          <div style={row3}>
            {hasCol('수량') && (
              <div>
                <label style={labelStyle}>수량</label>
                <input type="number" value={form.qty} onChange={e => set('qty', e.target.value)} style={inputStyle} />
              </div>
            )}
            {hasCol('단가') && (
              <div>
                <label style={labelStyle}>단가</label>
                <input type="number" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} style={inputStyle} />
              </div>
            )}
            {hasCol('자재비') && (
              <div>
                <label style={labelStyle}>자재비(자동계산)</label>
                <input value={formatNum(materialCost)} readOnly style={{ ...inputStyle, background:'#f8fafc', color:'#185FA5', fontWeight:700 }} />
              </div>
            )}
          </div>
        )}

        {specCol && (
          <>
            <label style={labelStyle}>{specCol === '상세사양' ? '상세사양 (가로 × 세로 × 두께, mm)' : 'SPEC (가로 × 세로 × 두께, mm)'}</label>
            <div style={row3}>
              <input type="number" value={form.width_mm} onChange={e => set('width_mm', e.target.value)} style={inputStyle} placeholder="가로" />
              <input type="number" value={form.height_mm} onChange={e => set('height_mm', e.target.value)} style={inputStyle} placeholder="세로" />
              <input type="number" value={form.thickness_mm} onChange={e => { set('thickness_mm', e.target.value); }} onBlur={lookupPrice} style={inputStyle} placeholder="두께" />
            </div>
          </>
        )}

        {(hasCol('발주처') || hasCol('입고예정일')) && (
          <div style={row2}>
            {hasCol('발주처') && (
              <div>
                <label style={labelStyle}>발주처(외주업체 등)</label>
                <input value={form.vendor} onChange={e => set('vendor', e.target.value)} style={inputStyle}
                  list="material-order-vendor-list" placeholder="업체명 (업체관리 등록 협력사 자동완성)" />
                <datalist id="material-order-vendor-list">
                  {(vendorCompanies || []).map(c => <option key={c.id} value={c.company_name} />)}
                </datalist>
              </div>
            )}
            {hasCol('입고예정일') && (
              <div>
                <label style={labelStyle}>입고예정일</label>
                <input type="date" value={form.expected_date} onChange={e => set('expected_date', e.target.value)} style={inputStyle} />
              </div>
            )}
          </div>
        )}

        {hasCol('상태') && (
          <div style={row2}>
            <div>
              <label style={labelStyle}>상태</label>
              <select value={form.in_status} onChange={e => set('in_status', e.target.value)} style={inputStyle}>
                <option value="발주">발주</option>
                <option value="입고완료">입고완료</option>
                <option value="취소">취소</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>입고일</label>
              <input type="date" value={form.in_date} onChange={e => set('in_date', e.target.value)} style={inputStyle} />
            </div>
          </div>
        )}

        {customColumnNames.length > 0 && (
          <div style={row2}>
            {customColumnNames.map(name => (
              <div key={name}>
                <label style={labelStyle}>{name}</label>
                <input value={customFields[name] || ''} onChange={e => updateCustomField(name, e.target.value)} style={inputStyle} />
              </div>
            ))}
          </div>
        )}

        <label style={labelStyle}>비고</label>
        <textarea value={form.note} onChange={e => set('note', e.target.value)} rows={2}
          style={{ ...inputStyle, marginBottom:16, resize:'vertical' }} />

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px', border:'1px solid #ddd', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 }}>
            취소
          </button>
          <button onClick={submit} disabled={saving}
            style={{ flex:1, padding:'11px', border:'none', borderRadius:8, background: saving?'#aaa':'#185FA5', color:'#fff', cursor: saving?'not-allowed':'pointer', fontSize:14, fontWeight:600 }}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 재질별 단가표 관리 모달 ──────────────────────────────────────────────
function MaterialUnitPriceManager({ onClose, categories, onCategoriesChanged }) {
  // 2026-09(단가표 구분 연동) — 원자재 탭 목록추가/삭제에서 관리하는 구분값(자재/부품 등)을
  // 단가표에도 반영한다. 재질만으로는 자재/부품이 같은 재질명을 쓸 수 있어 단가가 섞일 수 있으므로,
  // 구분을 별도 필드로 저장/조회한다.
  const categoryOptions = (categories && categories.length) ? categories : ['자재', '부품'];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ material:'', category: categoryOptions[0] || '', thickness_min:'', thickness_max:'', ym:'', unit_price:'', note:'' });
  const [editingId, setEditingId] = useState(null);
  // 2026-09(원자재 탭 UI개선) — 모달 크기 860x900 + 헤더 X 닫기 + 인쇄/다운로드/업로드 버튼.
  const uploadInputRef = useRef(null);
  // 2026-09(원자재 탭 목록관리 재설계) — "목록추가/삭제" 버튼이 이제 표시열을 관리하게 되면서,
  // 구분값(자재/부품) 관리는 이 단가표 모달의 "구분 관리" 버튼으로 옮겨왔다.
  const MATERIAL_CATEGORY_GROUP_ID = 0;
  const [showCatManager, setShowCatManager] = useState(false);
  const [catItems, setCatItems] = useState([]);
  const loadCatItems = () => API.get('/stock/columns', { params: { group_id: MATERIAL_CATEGORY_GROUP_ID, category: '원자재' } })
    .then(r => setCatItems(r.data.items || [])).catch(() => {});

  const load = () => API.get('/stock/material-unit-prices').then(r => setRows(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const set = (k,v) => setForm(f => ({ ...f, [k]:v }));
  const resetForm = () => { setForm({ material:'', category: categoryOptions[0] || '', thickness_min:'', thickness_max:'', ym:'', unit_price:'', note:'' }); setEditingId(null); };

  const submit = () => {
    if (!form.material) { alert('재질은 필수입니다.'); return; }
    const req = editingId
      ? API.put('/stock/material-unit-prices/' + editingId, form)
      : API.post('/stock/material-unit-prices', form);
    req.then(() => { load(); resetForm(); }).catch(() => alert('저장 중 오류가 발생했습니다.'));
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ material:r.material||'', category:r.category || categoryOptions[0] || '', thickness_min:r.thickness_min??'', thickness_max:r.thickness_max??'', ym:r.ym||'', unit_price:r.unit_price??'', note:r.note||'' });
  };

  const remove = (id) => {
    if (!window.confirm('이 단가 항목을 삭제하시겠습니까?')) return;
    API.delete('/stock/material-unit-prices/' + id).then(load).catch(()=>{});
  };

  // ── 인쇄: 현재 목록을 표로 구성해 새 창에서 인쇄합니다.
  const handlePrintTable = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const rowsHtml = rows.map(r => `
      <tr>
        <td>${r.category || '-'}</td>
        <td>${r.material || ''}</td>
        <td>${(r.thickness_min || r.thickness_max) ? `${r.thickness_min || 0}~${r.thickness_max || ''}` : '-'}</td>
        <td>${r.ym || '-'}</td>
        <td style="text-align:right">${formatNum(r.unit_price)}</td>
        <td>${r.note || '-'}</td>
      </tr>`).join('');
    w.document.write(`
      <html><head><title>재질별 단가표</title>
      <style>
        body{font-family:'Malgun Gothic',sans-serif;padding:20px;}
        h2{font-size:18px;margin-bottom:12px;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
        th,td{border:1px solid #ccc;padding:8px;text-align:center;}
        th{background:#f8fafc;}
      </style></head><body>
      <h2>재질별 단가표</h2>
      <table><thead><tr><th>구분</th><th>재질</th><th>두께범위</th><th>적용년월</th><th>단가</th><th>비고</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
    logPrint('재고관리', '재질별 단가표');
  };

  // ── 다운로드: 현재 목록을 xlsx로 내보냅니다.
  const handleDownloadTable = () => {
    const data = rows.map(r => ({
      구분: r.category || '',
      재질: r.material || '',
      '두께(최소)': r.thickness_min ?? '',
      '두께(최대)': r.thickness_max ?? '',
      적용년월: r.ym || '',
      단가: r.unit_price ?? '',
      비고: r.note || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '단가표');
    const filename = `재질별_단가표_${getKstDateString()}.xlsx`;
    XLSX.writeFile(wb, filename);
    logDownload('재고관리', filename);
  };

  // ── 업로드: xlsx 파일을 읽어 재질/두께/적용년월/단가/비고 열을 그대로 일괄 등록합니다.
  const handleUploadFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type:'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws);
        const items = json.map(row => ({
          material: row['재질'] || '',
          category: row['구분'] || '',
          thickness_min: row['두께(최소)'] ?? '',
          thickness_max: row['두께(최대)'] ?? '',
          ym: row['적용년월'] || '',
          unit_price: row['단가'] ?? '',
          note: row['비고'] || '',
        })).filter(it => it.material);
        if (!items.length) { alert('업로드할 데이터가 없습니다.'); return; }
        Promise.all(items.map(it => API.post('/stock/material-unit-prices', it)))
          .then(() => { load(); alert(`${items.length}건 업로드되었습니다.`); })
          .catch(() => alert('업로드 중 일부 오류가 발생했습니다.'));
      } catch {
        alert('파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const inputStyle = { padding:'8px 10px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box' };
  const headerBtn = (bg) => ({ background:bg, color:'#fff', border:'none', borderRadius:6, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:1200, height:900, maxWidth:'95vw', maxHeight:'95vh', overflowY:'auto', boxSizing:'border-box' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>재질별 단가표 관리</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => { loadCatItems(); setShowCatManager(true); }} style={headerBtn('#7c3aed')}>⚙️ 구분 관리</button>
            <button onClick={handlePrintTable} style={headerBtn('#475569')}>🖨️ 인쇄</button>
            <button onClick={handleDownloadTable} style={headerBtn('#16a34a')}>📥 다운로드</button>
            <button onClick={() => uploadInputRef.current && uploadInputRef.current.click()} style={headerBtn('#185FA5')}>📤 업로드</button>
            <input ref={uploadInputRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleUploadFile} />
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'0.7fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr auto', gap:8, marginBottom:16, alignItems:'end' }}>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>구분</div>
            <select value={form.category} onChange={e=>set('category', e.target.value)} style={inputStyle}>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>재질 *</div>
            <input value={form.material} onChange={e=>set('material', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>두께(최소)</div>
            <input type="number" value={form.thickness_min} onChange={e=>set('thickness_min', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>두께(최대)</div>
            <input type="number" value={form.thickness_max} onChange={e=>set('thickness_max', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>적용년월</div>
            <input value={form.ym} onChange={e=>set('ym', e.target.value)} placeholder="2026-09" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>단가</div>
            <input type="number" value={form.unit_price} onChange={e=>set('unit_price', e.target.value)} style={inputStyle} />
          </div>
          <button onClick={submit} style={{ padding:'9px 14px', background:'#185FA5', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {editingId ? '수정' : '추가'}
          </button>
        </div>

        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['구분','재질','두께범위','적용년월','단가','비고',''].map(h => (
                <th key={h} style={{ padding:'8px', background:'#f8fafc', fontSize:12, color:'#666', borderBottom:'1px solid #eee', textAlign:'center' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign:'center', color:'#999', padding:20, fontSize:13 }}>등록된 단가가 없습니다.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ padding:'8px', textAlign:'center', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>{r.category||'-'}</td>
                <td style={{ padding:'8px', textAlign:'center', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>{r.material}</td>
                <td style={{ padding:'8px', textAlign:'center', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>
                  {(r.thickness_min||r.thickness_max) ? `${r.thickness_min||0}~${r.thickness_max||''}` : '-'}
                </td>
                <td style={{ padding:'8px', textAlign:'center', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>{r.ym||'-'}</td>
                <td style={{ padding:'8px', textAlign:'right', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>{formatNum(r.unit_price)}</td>
                <td style={{ padding:'8px', textAlign:'center', fontSize:13, borderBottom:'1px solid #f4f4f4' }}>{r.note||'-'}</td>
                <td style={{ padding:'8px', textAlign:'center', borderBottom:'1px solid #f4f4f4' }}>
                  <button onClick={()=>startEdit(r)} style={smallBtn('#185FA5')}>수정</button>{' '}
                  <button onClick={()=>remove(r.id)} style={smallBtn('#dc2626')}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCatManager && (
        <ColumnManager
          groupName=""
          category="원자재"
          items={catItems}
          onClose={() => setShowCatManager(false)}
          onSaved={() => { setShowCatManager(false); onCategoriesChanged && onCategoriesChanged(); }}
          onSave={(items, renames) => API.put('/stock/columns', { group_id: MATERIAL_CATEGORY_GROUP_ID, category: '원자재', items, renames })}
          priceRows={rows}
          onEditPriceRow={(r) => { startEdit(r); setShowCatManager(false); }}
          onDeletePriceRow={remove}
        />
      )}
    </div>
  );
}